/*
 * Copyright © 2026 Aundrea Gregg. All rights reserved.
 * Created: January 14, 2026
 */

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, Fragment } from "react";
import { createPortal } from "react-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  BookOpen,
  Flame,
  Award,
  MessageCircle,
  Save,
  History,
  Star,
  Edit,
  Trash2,
  Plus,
  FileText,
  Search,
  ArrowLeft,
  Link as LinkIcon,
  Sparkles,
  Home,
  Sun,
  Moon,
  Sprout,
  Share2,
  ArrowRight,
  Calendar,
  Users,
  PenTool,
  Library,
  BookMarked,
  User,
  ChevronDown,
  Clock,
  LogOut,
  ListChecks,
  Check,
  Camera,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
import { cn } from "./lib/utils";
import { supabase } from "./lib/supabase";
import {
  fetchChapterTextHighlights,
  insertTextHighlight,
  deleteOverlappingTextHighlights,
} from "./lib/verseHighlightsApi";
import { getOffsetsForStudySelection } from "./utils/selectionOffsets";
import { getVerseSegments } from "./utils/bibleVerses";
import Auth from "./components/Auth";
import PasswordResetModal from "./components/PasswordResetModal";
import { ChapterVerseReader } from "./components/ChapterVerseReader";
import { VerseHighlightMenu } from "./components/VerseHighlightMenu";

gsap.registerPlugin(ScrollTrigger);

// Render study content with **bold** as actual bold (no markdown lib)
function renderStudyContent(text) {
  if (!text) return null;
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-bold text-[#1E293B]">{part}</strong>
    ) : (
      part
    )
  );
}

const THEME_STORAGE_KEY = "firstlight_darkMode";
const TAB_STORAGE_KEY = "firstlight_lastTab";
const GUIDED_VIEW_STORAGE_KEY = "firstlight_lastGuidedView";
const VALID_TABS = ["home", "study", "library", "guided", "profile", "bible"];
const VALID_GUIDED_VIEWS = ["plans", "in_plan"];

/** Selection → start/end offsets in plain text order within `root` */
function getTextOffsetsInContainer(root, range) {
  if (!root || !range) return null;
  const anchor = range.commonAncestorContainer;
  if (!root.contains(anchor)) return null;
  try {
    const pre = document.createRange();
    pre.selectNodeContents(root);
    pre.setEnd(range.startContainer, range.startOffset);
    const start = pre.toString().length;
    const len = range.toString().length;
    return { start, end: start + len };
  } catch (_) {
    return null;
  }
}

/** Map selection to indices in `fullText`; DOM offsets can drift from API string — validate + fallback */
function resolveHighlightOffsets(root, range, fullText) {
  if (!fullText || !root || !range) return null;
  const rawSel = range.toString();
  if (rawSel.length < 3) return null;

  const dom = getTextOffsetsInContainer(root, range);
  if (dom && dom.end > dom.start) {
    const s = Math.max(0, Math.min(dom.start, fullText.length));
    const e = Math.max(s, Math.min(dom.end, fullText.length));
    const slice = fullText.slice(s, e);
    if (slice === rawSel) {
      return { start: s, end: e };
    }
    if (slice.trim() === rawSel.trim() && rawSel.trim().length >= 3) {
      return { start: s, end: e };
    }
  }

  let idx = fullText.indexOf(rawSel);
  if (idx >= 0) return { start: idx, end: idx + rawSel.length };

  const trimmed = rawSel.trim();
  if (trimmed.length >= 3) {
    idx = fullText.indexOf(trimmed);
    if (idx >= 0) return { start: idx, end: idx + trimmed.length };
  }

  return null;
}

function mergeHighlightRanges(prev, start, end) {
  const ranges = [...prev, { id: Date.now(), start, end }].sort((a, b) => a.start - b.start);
  const out = [];
  for (const r of ranges) {
    const s = Math.max(0, r.start);
    const e = Math.max(s, r.end);
    if (!out.length) {
      out.push({ id: r.id, start: s, end: e });
      continue;
    }
    const last = out[out.length - 1];
    if (s <= last.end) {
      last.end = Math.max(last.end, e);
    } else {
      out.push({ id: r.id, start: s, end: e });
    }
  }
  return out;
}

/**
 * API.bible chapter/passage HTML often uses verse spans; stripping tags with `.replace(/\s+/g,' ')`
 * collapses everything into one line and can glue verse numbers to words — so verse styling/spacing fails.
 */
function insertVerseLineBreaksInPlainText(text) {
  if (!text) return text;
  let t = text;
  // Verse number glued to previous letter or punctuation (incl. , and ; before next verse)
  t = t.replace(/([a-z\.\!\?\:;,])(\d{1,3})(\s+)(?=[A-Z])/gi, (m, a, num, sp, offset, full) => {
    const n = parseInt(num, 10);
    if (n < 1 || n > 176) return m;
    const tail = full.slice(offset + m.length, offset + m.length + 4);
    if (/^AD\b/i.test(tail) || /^BC\b/i.test(tail)) return m;
    return `${a}\n${num}${sp}`;
  });
  // Space before verse number + capitalized word (typical English verse start)
  t = t.replace(/(\s)(\d{1,3})(\s+)(?=[A-Z])/g, (m, sp, num, space, offset, full) => {
    const n = parseInt(num, 10);
    if (n < 1 || n > 176) return m;
    const tail = full.slice(offset + m.length, offset + m.length + 4);
    if (/^AD\b/i.test(tail) || /^BC\b/i.test(tail)) return m;
    return `\n${num}${space}`;
  });
  return t.replace(/\n{3,}/g, "\n\n");
}

function bibleApiHtmlToPlainText(html) {
  if (!html) return "";
  let s = String(html);
  s = s.replace(/<\/span>\s*<span/gi, "</span> <span");
  s = s.replace(/<span[^>]*\bverse\b[^>]*>/gi, "\n<span>");
  s = s.replace(/<sup[^>]*>\s*\d{1,3}\s*<\/sup>/gi, (m) => `\n${m}`);
  s = s.replace(/<\/p>\s*<p[^>]*>/gi, "</p>\n<p");
  s = s.replace(/<br\s*\/?>/gi, "\n");
  s = s.replace(/<\/div>\s*<div[^>]*>/gi, "</div>\n<div");
  s = s.replace(/<[^>]+>/g, " ");
  s = s.replace(/[^\S\n]+/g, " ");
  s = s.replace(/\n[\s\n]*/g, "\n");
  s = s.trim();
  return insertVerseLineBreaksInPlainText(s);
}

function filterHighlightsForSegment(highlights, segStart, segEnd) {
  if (!highlights?.length) return [];
  return highlights
    .map((h) => {
      const s = Math.max(h.start, segStart);
      const e = Math.min(h.end, segEnd);
      if (s >= e) return null;
      return { ...h, start: s - segStart, end: e - segStart };
    })
    .filter(Boolean);
}

function renderHighlightedSlice(text, highlights, segKey) {
  if (!text) return null;
  if (!highlights?.length) return <span key={segKey}>{text}</span>;
  const sorted = [...highlights].sort((a, b) => a.start - b.start);
  const parts = [];
  let cursor = 0;
  sorted.forEach((h) => {
    const s = Math.max(0, Math.min(h.start, text.length));
    const e = Math.max(s, Math.min(h.end, text.length));
    if (s >= e) return;
    if (cursor < s) {
      parts.push(
        <span key={`gap-${h.id}-${cursor}-${segKey}`}>{text.slice(cursor, s)}</span>
      );
    }
    parts.push(
      <mark
        key={`${h.id}-${segKey}`}
        className="rounded-sm bg-[#fef08a]/90 px-0.5 text-[#1E293B] [box-decoration-break:clone]"
      >
        {text.slice(s, e)}
      </mark>
    );
    cursor = e;
  });
  if (cursor < text.length) {
    parts.push(<span key={`tail-${segKey}`}>{text.slice(cursor)}</span>);
  }
  return parts;
}

function renderPassageWithHighlights(text, highlights) {
  if (!text) return null;
  const segments = getVerseSegments(text);
  if (segments.length <= 1) {
    return renderHighlightedSlice(text, highlights, "full");
  }
  return segments.map((seg, segIdx) => {
    const slice = text.slice(seg.start, seg.end);
    const local = filterHighlightsForSegment(highlights, seg.start, seg.end);
    return (
      <Fragment key={`vseg-${seg.start}-${seg.end}`}>
        {segIdx > 0 && (
          <span className="block h-1 max-h-1 shrink-0 leading-none" aria-hidden="true" />
        )}
        {renderHighlightedSlice(slice, local, `s${segIdx}`)}
      </Fragment>
    );
  });
}

const LightBibleStudy = () => {
  // Navigation & Mode State (restore from localStorage on refresh)
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") return "home";
    try {
      const s = localStorage.getItem(TAB_STORAGE_KEY);
      if (s && VALID_TABS.includes(s)) return s;
    } catch (_) {}
    return "home";
  });
  const [guidedView, setGuidedView] = useState(() => {
    if (typeof window === "undefined") return "plans";
    try {
      const s = localStorage.getItem(GUIDED_VIEW_STORAGE_KEY);
      if (s && VALID_GUIDED_VIEWS.includes(s)) return s;
    } catch (_) {}
    return "plans";
  }); // "plans" | "in_plan"
  const [guidedCategory, setGuidedCategory] = useState("all"); // all | new_testament | old_testament | topical | community
  const [activeGuidedPlan, setActiveGuidedPlan] = useState(null);
  const [activePlanProgress, setActivePlanProgress] = useState(null); // { planId, planTitle, totalDays, currentDay, completedDays: number[] } — saved locally via localStorage
  const [guidedDaySession, setGuidedDaySession] = useState(null); // { planId, day, sectionIndex, scriptureIndex } when in a day's study flow
  const [guidedSessionVerseText, setGuidedSessionVerseText] = useState(null);
  const [guidedSessionVerseLoading, setGuidedSessionVerseLoading] = useState(false);
  const [guidedSessionReflect, setGuidedSessionReflect] = useState("");
  const [guidedSessionActivation, setGuidedSessionActivation] = useState("");
  const [guidedSessionSectionResponse, setGuidedSessionSectionResponse] = useState("");
  const [studyContext, setStudyContext] = useState(null);
  const [navigationHistory, setNavigationHistory] = useState([]);
  
  // Refs for GSAP animations
  const navRef = useRef(null);
  /** Home tab chat messages scroller (primary scroll surface; window often stays still). */
  const homeChatScrollRef = useRef(null);
  /** Ask input: grows with content up to max height, then scrolls. */
  const askTextareaRef = useRef(null);
  const mainContentRef = useRef(null);
  const tabContentRef = useRef(null);
  /** Skips first-name prompt when user arrived via password-recovery flow. */
  const passwordRecoveryActiveRef = useRef(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return localStorage.getItem(THEME_STORAGE_KEY) === "1";
    } catch (_) {
      return false;
    }
  });
  const [isVersesPaused, setIsVersesPaused] = useState(false);
  const cardsRef = useRef([]);
  const headerRef = useRef(null);

  /** Top bar slides away on scroll down; scroll-up or near-top shows it again. */
  const [navBarVisible, setNavBarVisible] = useState(true);
  const lastWindowScrollY = useRef(0);
  const lastHomeChatScrollY = useRef(0);

  /** Bottom tab bar shrinks slightly on scroll down; full size on scroll up / near top */
  const [bottomTabsCompact, setBottomTabsCompact] = useState(false);
  const bibleReadScrollRef = useRef(null);
  /** Top of Study-tab Bible reader — scroll here on chapter change (not whole page). */
  const bibleReaderTopRef = useRef(null);
  const lastBibleReadScrollTop = useRef(0);
  /** Selectable passage text: Bible tab + Home reading view */
  const bibleTabPassageRef = useRef(null);
  const homeReadingPassageRef = useRef(null);
  /** Persistent yellow highlights in Bible tab passage (offsets into readContent.text) */
  const [passageHighlights, setPassageHighlights] = useState([]);
  /** Study chapter reader: partial highlights (verse -> rows with offsets + color) */
  const [chapterVerseHighlights, setChapterVerseHighlights] = useState({});
  /** Floating highlight menu: selection rect + one segment per verse touched */
  const [verseHighlightMenu, setVerseHighlightMenu] = useState(null);
  /** Outer shell — some WebViews scroll this instead of window */
  const appShellRef = useRef(null);
  /** Top of <main> — scrollIntoView pulls iOS/WKWebView to the real top */
  const tabTopAnchorRef = useRef(null);

  // Study State
  const [studyInput, setStudyInput] = useState("");
  /** Legacy name kept so any stale bundle/HMR never hits ReferenceError; UI uses unified field + prompt only. */
  const [studyInputType] = useState("reference");
  const [currentStudy, setCurrentStudy] = useState(null);
  const [isStudyLoading, setIsStudyLoading] = useState(false);

  // Ask State
  const [askInput, setAskInput] = useState("");
  const [currentAnswer, setCurrentAnswer] = useState(null);
  const [isAskLoading, setIsAskLoading] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState(null);

  const syncAskTextareaHeight = useCallback(() => {
    const el = askTextareaRef.current;
    if (!el) return;
    const minH =
      typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches
        ? 56
        : 44;
    const maxH = 220;
    el.style.height = "auto";
    const next = Math.min(Math.max(el.scrollHeight, minH), maxH);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxH ? "auto" : "hidden";
  }, []);

  useLayoutEffect(() => {
    syncAskTextareaHeight();
  }, [askInput, syncAskTextareaHeight]);

  // Read State
  const [readPassage, setReadPassage] = useState("");
  const [readContent, setReadContent] = useState(null);
  const [selectedVerses, setSelectedVerses] = useState([]);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [bibleSearch, setBibleSearch] = useState("");
  const [isReadingPage, setIsReadingPage] = useState(false);
  const [currentTestament, setCurrentTestament] = useState("OT");

  // Authentication State
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [userFirstName, setUserFirstName] = useState('');
  const [profileAvatarUrl, setProfileAvatarUrl] = useState(null);
  const profileAvatarInputRef = useRef(null);

  // Gamification State
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [lastStudyDate, setLastStudyDate] = useState(null);
  const [totalStudyDays, setTotalStudyDays] = useState(0);
  const [badges, setBadges] = useState([]);

  // Library State (no demo data — user content only)
  const [savedStudies, setSavedStudies] = useState([]);
  const [savedAnswers, setSavedAnswers] = useState([]);
  const [studyHistory, setStudyHistory] = useState([]);
  const [notes, setNotes] = useState([]);

  // Notes Editor State
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [libraryView, setLibraryView] = useState("studies"); // "studies", "answers", "notes"
  const [currentNote, setCurrentNote] = useState(null);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [noteTags, setNoteTags] = useState("");
  const [noteSearch, setNoteSearch] = useState("");

  // Settings
  const [bibleVersion, setBibleVersion] = useState("NIV");
  const [isPremium, setIsPremium] = useState(false);
  const [usageCount, setUsageCount] = useState({
    studies: 0,
    asks: 0,
    weekStart: null,
  });

  // Constants
  const WEEKLY_LIMITS = { studies: 10, asks: 5 };

  const bibleVersions = [
    { value: "KJV", label: "King James Version", premium: false, image: "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=400&h=600&fit=crop&q=80" },
    { value: "NIV", label: "New International Version", premium: false, image: "https://images.unsplash.com/photo-1512820790803-83ca750da8c7?w=400&h=600&fit=crop&q=80" },
    { value: "ESV", label: "English Standard Version", premium: false, image: "https://images.unsplash.com/photo-1543002588-bfa74002ed7e?w=400&h=600&fit=crop&q=80" },
    { value: "NLT", label: "New Living Translation", premium: false, image: "https://images.unsplash.com/photo-1512820790803-83ca750da8c7?w=400&h=600&fit=crop&q=80" },
  ];

  // Bible Books with Chapter Counts
  const bibleBooks = [
    // Old Testament
    { name: "Genesis", chapters: 50, testament: "OT" },
    { name: "Exodus", chapters: 40, testament: "OT" },
    { name: "Leviticus", chapters: 27, testament: "OT" },
    { name: "Numbers", chapters: 36, testament: "OT" },
    { name: "Deuteronomy", chapters: 34, testament: "OT" },
    { name: "Joshua", chapters: 24, testament: "OT" },
    { name: "Judges", chapters: 21, testament: "OT" },
    { name: "Ruth", chapters: 4, testament: "OT" },
    { name: "1 Samuel", chapters: 31, testament: "OT" },
    { name: "2 Samuel", chapters: 24, testament: "OT" },
    { name: "1 Kings", chapters: 22, testament: "OT" },
    { name: "2 Kings", chapters: 25, testament: "OT" },
    { name: "1 Chronicles", chapters: 29, testament: "OT" },
    { name: "2 Chronicles", chapters: 36, testament: "OT" },
    { name: "Ezra", chapters: 10, testament: "OT" },
    { name: "Nehemiah", chapters: 13, testament: "OT" },
    { name: "Esther", chapters: 10, testament: "OT" },
    { name: "Job", chapters: 42, testament: "OT" },
    { name: "Psalm", chapters: 150, testament: "OT" },
    { name: "Proverbs", chapters: 31, testament: "OT" },
    { name: "Ecclesiastes", chapters: 12, testament: "OT" },
    { name: "Song of Solomon", chapters: 8, testament: "OT" },
    { name: "Isaiah", chapters: 66, testament: "OT" },
    { name: "Jeremiah", chapters: 52, testament: "OT" },
    { name: "Lamentations", chapters: 5, testament: "OT" },
    { name: "Ezekiel", chapters: 48, testament: "OT" },
    { name: "Daniel", chapters: 12, testament: "OT" },
    { name: "Hosea", chapters: 14, testament: "OT" },
    { name: "Joel", chapters: 3, testament: "OT" },
    { name: "Amos", chapters: 9, testament: "OT" },
    { name: "Obadiah", chapters: 1, testament: "OT" },
    { name: "Jonah", chapters: 4, testament: "OT" },
    { name: "Micah", chapters: 7, testament: "OT" },
    { name: "Nahum", chapters: 3, testament: "OT" },
    { name: "Habakkuk", chapters: 3, testament: "OT" },
    { name: "Zephaniah", chapters: 3, testament: "OT" },
    { name: "Haggai", chapters: 2, testament: "OT" },
    { name: "Zechariah", chapters: 14, testament: "OT" },
    { name: "Malachi", chapters: 4, testament: "OT" },
    // New Testament
    { name: "Matthew", chapters: 28, testament: "NT" },
    { name: "Mark", chapters: 16, testament: "NT" },
    { name: "Luke", chapters: 24, testament: "NT" },
    { name: "John", chapters: 21, testament: "NT" },
    { name: "Acts", chapters: 28, testament: "NT" },
    { name: "Romans", chapters: 16, testament: "NT" },
    { name: "1 Corinthians", chapters: 16, testament: "NT" },
    { name: "2 Corinthians", chapters: 13, testament: "NT" },
    { name: "Galatians", chapters: 6, testament: "NT" },
    { name: "Ephesians", chapters: 6, testament: "NT" },
    { name: "Philippians", chapters: 4, testament: "NT" },
    { name: "Colossians", chapters: 4, testament: "NT" },
    { name: "1 Thessalonians", chapters: 5, testament: "NT" },
    { name: "2 Thessalonians", chapters: 3, testament: "NT" },
    { name: "1 Timothy", chapters: 6, testament: "NT" },
    { name: "2 Timothy", chapters: 4, testament: "NT" },
    { name: "Titus", chapters: 3, testament: "NT" },
    { name: "Philemon", chapters: 1, testament: "NT" },
    { name: "Hebrews", chapters: 13, testament: "NT" },
    { name: "James", chapters: 5, testament: "NT" },
    { name: "1 Peter", chapters: 5, testament: "NT" },
    { name: "2 Peter", chapters: 3, testament: "NT" },
    { name: "1 John", chapters: 5, testament: "NT" },
    { name: "2 John", chapters: 1, testament: "NT" },
    { name: "3 John", chapters: 1, testament: "NT" },
    { name: "Jude", chapters: 1, testament: "NT" },
    { name: "Revelation", chapters: 22, testament: "NT" },
  ];

  const badgeDefinitions = [
    {
      id: "first_study",
      name: "First Step",
      description: "Complete your first study",
      icon: "🌱",
      requirement: 1,
      type: "days",
    },
    {
      id: "streak_3",
      name: "Committed",
      description: "3-day study streak",
      icon: "🔥",
      requirement: 3,
      type: "streak",
    },
    {
      id: "streak_7",
      name: "Devoted",
      description: "7-day study streak",
      icon: "⭐",
      requirement: 7,
      type: "streak",
    },
    {
      id: "streak_30",
      name: "Faithful",
      description: "30-day study streak",
      icon: "👑",
      requirement: 30,
      type: "streak",
    },
    {
      id: "days_10",
      name: "Student",
      description: "Study for 10 total days",
      icon: "📖",
      requirement: 10,
      type: "days",
    },
    {
      id: "days_50",
      name: "Scholar",
      description: "Study for 50 total days",
      icon: "🎓",
      requirement: 50,
      type: "days",
    },
    {
      id: "deep_diver",
      name: "Deep Diver",
      description: "Follow 3 related passages",
      icon: "🤿",
      requirement: 3,
      type: "connected",
    },
    {
      id: "curious_mind",
      name: "Curious Mind",
      description: "Ask 10 questions",
      icon: "🧠",
      requirement: 10,
      type: "asks",
    },
  ];

  // More plans coming soon — only featured plan (Worship) is in the app for now
  const guidedStudyPlans = [];

  const planDaysConfig = {
    worship_priority: [
      {
        day: 1,
        title: "Opening Reflection",
        scriptureRef: "Mark 12:30",
        quote: "Before we learn how to worship effectively, we must understand when worship happens. Before we go deeper, let’s start with this question:",
        sections: ["Scripture", "Guided Reflection", "Personal Activation"],
        guidedReflection: `What is worship?

Pause. Don't rush this.
Don't give the church answer.
Give the honest one.

There is no point in knowing what worship is if we have not made it the priority of our lives.

This is not a surface conversation.
This is not about music preference.
This is not about Sunday attendance.

This is about hierarchy.

Worship reveals what sits highest in your heart.

If worship is occasional, then God is occasional.
If worship is emotional, then God is seasonal.
If worship is convenient, then God is secondary.`,
        personalActivationPrompt: "Write your definition of worship down.\nSit with it.\nYour answer exposes your priority.\nAnd whatever holds priority holds power.",
      },
      {
        day: 2,
        title: "Reverence — The Foundation of Worship",
        scriptureRef: "Psalm 103:11–14, Proverbs 1:7, Proverbs 8:13, Ecclesiastes 12:13",
        quote: "The fear of the Lord does not mean being afraid of Him. It means reverence: deep respect, deep honor, deep awareness of who He is. Without reverence, God cannot hold the rightful place in our hearts.",
        sections: ["Scripture", "Guided Reflection", "Personal Activation"],
        guidedReflection: `What Does It Mean to Fear the Lord?
The "fear of the Lord" does not mean being afraid of Him.
It means reverence.
Deep respect.
Deep honor.
Deep awareness of who He is.
Without reverence, God cannot hold the rightful place in our hearts.

Psalm 103 says His love is great toward those who fear Him.
If reverence positions us to receive His compassion, what happens when we don't fear Him?

It's not that He withholds love.
It's that our posture prevents us from receiving it.

If we fear natural things more than we fear God — money, reputation, relationships, deadlines — then those things sit higher in our hearts than He does.

And whatever sits highest receives the most worship.`,
        personalActivationPrompt: `Select one or more questions as a journal prompt:

1. What does reverence practically look like in your daily life?
2. What natural things do you respect more than God without realizing it?
3. How does the fear of the Lord protect your life?
4. In what areas have you treated God casually?`,
      },
      {
        day: 3,
        title: "The Reality of Time",
        scriptureRef: "Ephesians 5:15–16, Psalm 90:12, James 4:14, Psalm 39:4–5",
        quote: "Time does not belong to us. He created it, sustains it, governs it. The things you value most are revealed by the time you give them. Church attendance is not the same as personal devotion.",
        sections: ["Scripture", "Guided Reflection", "Personal Activation"],
        guidedReflection: `Once you truly respect God, you realize something:
Time does not belong to us.

He created it.
He sustains it.
He governs it.

We talk about time we lost.
We talk about time we have left.
But we rarely talk about maximizing the present moment.
In the scope of eternity, our lives are brief.
Not for our glory.
For His.

The things you value most are revealed by the time you give them.
So, ask yourself:
• What does your prayer life actually look like?
• What is the first thing you do when you wake up?
• What is the last thing you do before sleep?
• What fills your mind in idle moments?
• How intentional are you about alone time with God?
• How much time do you spend studying His Word for clarity and revelation?

Church attendance is not the same as personal devotion.
Going to church gives you tools.
Revelation comes from relationship.
God may confirm something through someone else
but He will speak it to you first.

You cannot authentically worship someone you do not intentionally know.`,
        personalActivationPrompt: `Select one or more questions as a journal prompt:

1. What does your time allocation reveal about your priorities?
2. How can you intentionally redeem time this week?
3. What distractions consistently compete with your time with God?
4. What would change if you started and ended every day with Him?`,
      },
      {
        day: 4,
        title: "The Power of Yes and No",
        scriptureRef: "Matthew 5:37, Numbers 11:11–17",
        quote: "Worship requires boundaries. You cannot put God first if everyone else has unrestricted access to you. Boundaries are not rebellion. They are stewardship.",
        sections: ["Scripture", "Guided Reflection", "Personal Activation"],
        guidedReflection: `Worship requires boundaries.
You cannot put God first if everyone else has unrestricted access to you.

If saying "yes" to everything drains you, you are not being Christlike — you are being unwise.

Even Moses reached his capacity. He told the Lord he was overwhelmed. And God responded with provision.

Boundaries are not rebellion. They are stewardship.

If something interferes with your time with Him, you must say no.
You cannot worship properly without order.

And sometimes the first person you must put in their place is yourself.`,
        personalActivationPrompt: `Select one or more questions as a journal prompt:

1. Where do you struggle to say no?
2. What commitments are interfering with your time with God?
3. How can boundaries actually deepen worship?
4. What would shift if you protected your devotional time like an appointment?`,
      },
      {
        day: 5,
        title: "Closing Reflection",
        scriptureRef: "—",
        quote: "Worship becomes ultimate when God is undeniably first. Not in what I say — but in how I schedule. Not in how I feel — but in how I decide. Worship is not proven in song. It is proven in surrender.",
        sections: ["Guided Reflection", "Personal Activation", "Prayer"],
        guidedReflection: `Worship becomes the ultimate priority when:
We cultivate reverence.
We recognize time belongs to Him.
We protect our devotion with boundaries.
We intentionally pursue relationship.
Worship is not emotional.
It is positional.

When God is first in your time, first in your decisions, first in your yes and no — worship is happening.

Ask yourself:
Where does God truly sit in my life?

Not in what I say —
but in how I schedule.

Not in how I feel —
but in how I decide.
Not in my intentions —
but in my discipline.

If someone examined your calendar, your conversations, your spending, your thoughts
would they conclude that God is first?

Worship is not proven in song. It is proven in surrender. And surrender always costs something.
Move something. Cancel something. Rearrange something. Commit to something.

Do not leave convicted.
Leave reordered.

Worship becomes ultimate when God is undeniably first.

Re-evaluate your life. And move Him back to the top — where He has always belonged.`,
        personalActivationPrompt: `This week try the following actions:

1. Choose a daily set time for uninterrupted worship.
2. Remove one distraction that consistently interrupts your devotion.
3. Practice saying one intentional "no" to protect time with God.
4. Begin and end each day acknowledging Him.`,
      },
    ],
  };

  // Auto-fetch scripture when entering or changing scripture step in guided day session
  useEffect(() => {
    if (!guidedDaySession || !activePlanProgress) return;
    const daysConfig = planDaysConfig[guidedDaySession.planId] || [];
    const d = daysConfig.find((x) => x.day === guidedDaySession.day);
    if (!d) return;
    const sections = d.sections || [];
    const sectionName = sections[guidedDaySession.sectionIndex];
    const isScripture = sectionName === "Scripture" || (sectionName && sectionName.toLowerCase().includes("scripture"));
    if (!isScripture) return;
    const refs = (d.scriptureRef || "").split(",").map((s) => s.trim()).filter((s) => s && s !== "—");
    const ref = refs[guidedDaySession.scriptureIndex];
    if (ref) fetchPassageForGuided(ref);
    else setGuidedSessionVerseText(null);
  }, [guidedDaySession?.planId, guidedDaySession?.day, guidedDaySession?.sectionIndex, guidedDaySession?.scriptureIndex, activePlanProgress?.planId]);

  // Check auth state on mount
  useEffect(() => {
    let mounted = true;

    // Check current session
    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        if (error) {
          console.error('Auth error:', error);
        }
        if (mounted) {
          setUser(session?.user ?? null);
          if (session?.user && typeof window !== 'undefined') {
            const hash = window.location.hash.replace(/^#/, '');
            if (hash) {
              const params = new URLSearchParams(hash);
              if (params.get('type') === 'recovery') {
                passwordRecoveryActiveRef.current = true;
                setShowPasswordReset(true);
                setShowAuth(false);
              }
            }
          }
          setAuthLoading(false);
        }
      })
      .catch((error) => {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setAuthLoading(false);
        }
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (mounted) {
        const currentUser = session?.user ?? null;
        setUser(currentUser);

        if (event === 'PASSWORD_RECOVERY' && session?.user) {
          passwordRecoveryActiveRef.current = true;
          setShowPasswordReset(true);
          setShowAuth(false);
        }

        // Load user's first name if available
        if (currentUser) {
          const firstName = currentUser.user_metadata?.first_name ||
                          currentUser.user_metadata?.firstName ||
                          localStorage.getItem(`userFirstName_${currentUser.id}`);
          if (firstName) {
            setUserFirstName(firstName);
          } else if (session && event === 'SIGNED_IN') {
            const skipNamePrompt = passwordRecoveryActiveRef.current;
            if (passwordRecoveryActiveRef.current) {
              passwordRecoveryActiveRef.current = false;
            }
            if (!skipNamePrompt) {
              setTimeout(() => {
                if (!localStorage.getItem(`userFirstName_${currentUser.id}`)) {
                  setShowNamePrompt(true);
                }
              }, 500);
            }
          }
        }

        if (session?.user && event !== 'PASSWORD_RECOVERY') {
          setShowAuth(false);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Profile photo: Supabase user metadata URL, or local data URL fallback (per device)
  useEffect(() => {
    if (!user?.id) {
      setProfileAvatarUrl(null);
      return;
    }
    try {
      const fromMeta = user.user_metadata?.avatar_url;
      const fromLocal = localStorage.getItem(`profileAvatarLocal_${user.id}`);
      setProfileAvatarUrl(fromMeta || fromLocal || null);
    } catch (_) {
      setProfileAvatarUrl(user.user_metadata?.avatar_url || null);
    }
  }, [user?.id, user?.user_metadata?.avatar_url]);

  // Storage key: per-user when logged in, shared when guest
  const storageKey = user?.id ? `lightBibleStudyData_${user.id}` : "lightBibleStudyData";

  // Persist active tab and guided view so refresh keeps user on same page
  useEffect(() => {
    try {
      if (VALID_TABS.includes(activeTab)) {
        localStorage.setItem(TAB_STORAGE_KEY, activeTab);
      }
      if (activeTab === "guided" && VALID_GUIDED_VIEWS.includes(guidedView)) {
        localStorage.setItem(GUIDED_VIEW_STORAGE_KEY, guidedView);
      }
    } catch (_) {}
  }, [activeTab, guidedView]);

  // Load data from localStorage (user-scoped when logged in)
  useEffect(() => {
    const savedData = localStorage.getItem(storageKey);
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        setCurrentStreak(data.currentStreak ?? 0);
        setLongestStreak(data.longestStreak ?? 0);
        setLastStudyDate(data.lastStudyDate ?? null);
        setTotalStudyDays(data.totalStudyDays ?? 0);
        setBadges(data.badges || []);
        if (data.savedStudies && data.savedStudies.length > 0) {
          const demoPassages = ["John 3:16", "Philippians 4:13", "Jeremiah 29:11", "Proverbs 3:5-6", "Romans 8:28", "Psalm 23:1-2"];
          const filteredStudies = data.savedStudies.filter((s) => !demoPassages.includes(s.passage));
          setSavedStudies(filteredStudies);
        }
        if (data.savedAnswers && data.savedAnswers.length > 0) {
          setSavedAnswers(data.savedAnswers);
        }
        if (data.studyHistory && data.studyHistory.length > 0) {
          setStudyHistory(data.studyHistory);
        }
        if (data.notes && data.notes.length > 0) {
          const demoNoteTitles = ["Reflections on Faith", "Prayer Insights", "Love and Forgiveness", "God's Promises", "The Fruit of the Spirit", "Walking in Wisdom"];
          const filteredNotes = data.notes.filter((n) => !demoNoteTitles.includes(n.title));
          setNotes(filteredNotes);
        }
        setUsageCount(
          data.usageCount && typeof data.usageCount === "object"
            ? data.usageCount
            : { studies: 0, asks: 0, weekStart: null }
        );
        setIsPremium(!!data.isPremium);
        if (data.guidedPlanProgress && typeof data.guidedPlanProgress === "object") {
          setActivePlanProgress(data.guidedPlanProgress);
        }
      } catch (_) {
        setCurrentStreak(0);
        setTotalStudyDays(0);
        setLastStudyDate(null);
      }
    } else if (user?.id) {
      // New logged-in user: start with clean counts
      setCurrentStreak(0);
      setLongestStreak(0);
      setLastStudyDate(null);
      setTotalStudyDays(0);
    }
    // Grant unlimited access if Supabase user metadata has is_premium (e.g. test accounts)
    if (user?.user_metadata?.is_premium) {
      setIsPremium(true);
    }
    checkStreak();
  }, [storageKey, user?.id, user?.user_metadata?.is_premium]);

  // GSAP Animations on mount - store tweens and kill on cleanup
  useEffect(() => {
    const tweens = [];

    if (navRef.current) {
      tweens.push(gsap.fromTo(
        navRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.3, ease: "power2.out" }
      ));
    }
    if (mainContentRef.current) {
      tweens.push(gsap.fromTo(
        mainContentRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.3, delay: 0.1, ease: "power2.out" }
      ));
    }

    const progressBars = Array.from(document.querySelectorAll('.gsap-progress')).filter(el => el != null);
    progressBars.forEach((bar) => {
      if (bar) {
        gsap.set(bar, { width: '0%' });
        const targetWidth = bar.style.width || '0%';
        tweens.push(gsap.to(bar, {
          width: targetWidth,
          duration: 0.5,
          ease: "power2.out"
        }));
      }
    });

    const scrollElements = Array.from(document.querySelectorAll('.gsap-scroll')).filter(el => el != null);
    scrollElements.forEach((el) => {
      if (el) {
        tweens.push(gsap.fromTo(
          el,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 0.4,
            ease: "power2.out",
            scrollTrigger: {
              trigger: el,
              start: "top 85%",
              toggleActions: "play none none none",
            },
          }
        ));
      }
    });

    return () => {
      tweens.forEach(t => t.kill());
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const SCROLL_DELTA = 12;

  const scrollActiveTabToTop = () => {
    const html = document.documentElement;
    const prevHtmlSb = html.style.scrollBehavior;
    const prevBodySb = document.body.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    document.body.style.scrollBehavior = "auto";
    try {
      const se = document.scrollingElement;
      if (se) {
        se.scrollTop = 0;
        se.scrollLeft = 0;
      }
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      const rootEl = document.getElementById("root");
      if (rootEl) rootEl.scrollTop = 0;
      if (appShellRef.current) appShellRef.current.scrollTop = 0;
      if (mainContentRef.current) mainContentRef.current.scrollTop = 0;
      if (tabContentRef.current) tabContentRef.current.scrollTop = 0;
      if (homeChatScrollRef.current) homeChatScrollRef.current.scrollTop = 0;
      if (bibleReadScrollRef.current) bibleReadScrollRef.current.scrollTop = 0;
      tabTopAnchorRef.current?.scrollIntoView({
        block: "start",
        inline: "nearest",
        behavior: "auto",
      });
      lastWindowScrollY.current =
        window.scrollY || document.documentElement.scrollTop || 0;
      lastHomeChatScrollY.current = homeChatScrollRef.current?.scrollTop ?? 0;
      lastBibleReadScrollTop.current = bibleReadScrollRef.current?.scrollTop ?? 0;
    } finally {
      html.style.scrollBehavior = prevHtmlSb;
      document.body.style.scrollBehavior = prevBodySb;
    }
  };

  // Each tab (Home, Study, Library, Guided, Profile) should open at the top.
  useLayoutEffect(() => {
    setNavBarVisible(true);
    setBottomTabsCompact(false);
    scrollActiveTabToTop();
    let cancelled = false;
    let raf2 = 0;
    const id = requestAnimationFrame(() => {
      if (cancelled) return;
      scrollActiveTabToTop();
      raf2 = requestAnimationFrame(() => {
        if (!cancelled) scrollActiveTabToTop();
      });
    });
    const t0 = window.setTimeout(() => {
      if (!cancelled) scrollActiveTabToTop();
    }, 0);
    const t1 = window.setTimeout(() => {
      if (!cancelled) scrollActiveTabToTop();
    }, 50);
    const t2 = window.setTimeout(() => {
      if (!cancelled) scrollActiveTabToTop();
    }, 200);
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
      cancelAnimationFrame(raf2);
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [activeTab]);

  useLayoutEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, darkMode ? "1" : "0");
    } catch (_) {}
    const meta = document.getElementById("theme-color-meta");
    if (meta) {
      meta.setAttribute("content", darkMode ? "#0a1628" : "#F0F4F8");
    }
  }, [darkMode]);

  useEffect(() => {
    const onWindowScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      const delta = y - lastWindowScrollY.current;
      lastWindowScrollY.current = y;
      if (y <= SCROLL_DELTA) {
        setNavBarVisible(true);
        setBottomTabsCompact(false);
      } else if (delta > SCROLL_DELTA) {
        setNavBarVisible(false);
        setBottomTabsCompact(true);
      } else if (delta < -SCROLL_DELTA) {
        setNavBarVisible(true);
        setBottomTabsCompact(false);
      }
    };

    window.addEventListener("scroll", onWindowScroll, { passive: true });
    return () => window.removeEventListener("scroll", onWindowScroll);
  }, []);

  useLayoutEffect(() => {
    if (activeTab !== "home") return undefined;
    const el = homeChatScrollRef.current;
    if (!el) return undefined;

    const onHomeScroll = () => {
      const y = el.scrollTop;
      const delta = y - lastHomeChatScrollY.current;
      lastHomeChatScrollY.current = y;
      if (y <= SCROLL_DELTA) {
        setNavBarVisible(true);
        setBottomTabsCompact(false);
      } else if (delta > SCROLL_DELTA) {
        setNavBarVisible(false);
        setBottomTabsCompact(true);
      } else if (delta < -SCROLL_DELTA) {
        setNavBarVisible(true);
        setBottomTabsCompact(false);
      }
    };

    lastHomeChatScrollY.current = el.scrollTop;
    el.addEventListener("scroll", onHomeScroll, { passive: true });
    return () => el.removeEventListener("scroll", onHomeScroll);
  }, [activeTab, currentAnswer, askInput, pendingQuestion]);

  // Compact bottom tab bar when scrolling down in Bible reading pane (same idea as window scroll).
  useEffect(() => {
    if (!isReadingPage) {
      return;
    }

    const el = bibleReadScrollRef.current;
    if (!el) return;

    lastBibleReadScrollTop.current = el.scrollTop ?? 0;
    const threshold = 12;

    const onBibleScroll = () => {
      const y = el.scrollTop ?? 0;
      const delta = y - lastBibleReadScrollTop.current;
      lastBibleReadScrollTop.current = y;

      if (y <= threshold) setBottomTabsCompact(false);
      else if (delta > threshold) setBottomTabsCompact(true);
      else if (delta < -threshold) setBottomTabsCompact(false);
    };

    el.addEventListener("scroll", onBibleScroll, { passive: true });
    return () => el.removeEventListener("scroll", onBibleScroll);
  }, [isReadingPage, readContent]);

  // Bible reader: after chapter loads/changes, align viewport with top of reader only (not document top)
  useLayoutEffect(() => {
    if (!isReadingPage || !readContent?.passage) return;
    const run = () => {
      if (bibleReadScrollRef.current) bibleReadScrollRef.current.scrollTop = 0;
      lastBibleReadScrollTop.current = 0;
      bibleReaderTopRef.current?.scrollIntoView({ block: "start", behavior: "auto" });
    };
    run();
    const id = requestAnimationFrame(run);
    return () => cancelAnimationFrame(id);
  }, [isReadingPage, readContent?.passage]);









  // Save data to localStorage (user-scoped when logged in)
  useEffect(() => {
    const data = {
      currentStreak,
      longestStreak,
      lastStudyDate,
      totalStudyDays,
      badges,
      savedStudies,
      savedAnswers,
      studyHistory,
      notes,
      usageCount,
      isPremium,
      guidedPlanProgress: activePlanProgress,
    };
    localStorage.setItem(storageKey, JSON.stringify(data));
  }, [
    storageKey,
    currentStreak,
    longestStreak,
    lastStudyDate,
    totalStudyDays,
    badges,
    savedStudies,
    savedAnswers,
    studyHistory,
    notes,
    usageCount,
    isPremium,
    activePlanProgress,
  ]);

  // Check and update streak (reset if more than 1 day gap since last study)
  const checkStreak = () => {
    const today = new Date().toDateString();
    if (lastStudyDate) {
      const lastDate = new Date(lastStudyDate);
      const diffDays = Math.floor(
        (new Date(today).getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (diffDays > 1) {
        setCurrentStreak(0);
      }
    }
  };

  // Re-run streak check when lastStudyDate changes (e.g. after load from storage)
  useEffect(() => {
    checkStreak();
  }, [lastStudyDate]);

  const updateStreak = () => {
    const today = new Date().toDateString();

    if (lastStudyDate !== today) {
      const yesterday = new Date(Date.now() - 86400000).toDateString();

      if (lastStudyDate === yesterday || lastStudyDate === null) {
        const newStreak = currentStreak + 1;
        setCurrentStreak(newStreak);
        setLongestStreak(Math.max(newStreak, longestStreak));
        checkForBadges("streak", newStreak);
      } else {
        setCurrentStreak(1);
        checkForBadges("streak", 1);
      }

      setLastStudyDate(today);
      const newTotalDays = totalStudyDays + 1;
      setTotalStudyDays(newTotalDays);
      checkForBadges("days", newTotalDays);
    }
  };

  const checkForBadges = (type, value) => {
    badgeDefinitions.forEach((badge) => {
      if (
        badge.type === type &&
        !badges.includes(badge.id) &&
        value >= badge.requirement
      ) {
        setBadges([...badges, badge.id]);
        showBadgeNotification(badge);
      }
    });
  };

  const showBadgeNotification = (badge) => {
    console.log(`🎉 New badge earned: ${badge.name}`);
  };

  // Usage tracking for freemium (disabled for now — unlimited access)
  const checkUsageLimits = (_action) => {
    return true;
  };

  const incrementUsage = (action) => {
    if (isPremium) return;

    setUsageCount((prev) => ({
      ...prev,
      [action === "study" ? "studies" : "asks"]:
        prev[action === "study" ? "studies" : "asks"] + 1,
    }));
  };

  // Authentication handlers
  const handleAuthSuccess = async (userData) => {
    setUser(userData);
    setShowAuth(false);
    
    // Check if user has a first name in metadata
    const firstName = userData?.user_metadata?.first_name || 
                      userData?.user_metadata?.firstName ||
                      localStorage.getItem(`userFirstName_${userData?.id}`);
    
    if (!firstName) {
      // Show name prompt if no first name exists
      setShowNamePrompt(true);
    } else {
      setUserFirstName(firstName);
    }
  };

  // Handle name submission
  const handleNameSubmit = async (firstName) => {
    if (!firstName.trim()) return;
    
    const trimmedName = firstName.trim();
    setUserFirstName(trimmedName);
    
    // Store in localStorage
    if (user?.id) {
      localStorage.setItem(`userFirstName_${user.id}`, trimmedName);
    }
    
    // Update user metadata in Supabase
    if (user) {
      try {
        const { error } = await supabase.auth.updateUser({
          data: { first_name: trimmedName }
        });
        if (error) {
          console.error('Error updating user metadata:', error);
        }
      } catch (error) {
        console.error('Error updating user metadata:', error);
      }
    }
    
    setShowNamePrompt(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  const handleProfileAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !user?.id) return;
    if (!file.type.startsWith("image/")) return;
    if (file.size > 2 * 1024 * 1024) {
      window.alert("Please choose an image under 2 MB.");
      return;
    }

    const ext = (file.name.split(".").pop() || "jpg").slice(0, 8);
    const path = `${user.id}/avatar.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("avatars")
      .upload(path, file, { upsert: true, contentType: file.type || "image/jpeg" });

    if (!uploadError) {
      const {
        data: { publicUrl },
      } = supabase.storage.from("avatars").getPublicUrl(path);
      const { error: metaError } = await supabase.auth.updateUser({
        data: {
          ...user.user_metadata,
          avatar_url: publicUrl,
        },
      });
      if (metaError) {
        console.error("Error saving avatar URL:", metaError);
      }
      try {
        localStorage.removeItem(`profileAvatarLocal_${user.id}`);
      } catch (_) {}
      setProfileAvatarUrl(publicUrl);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== "string") return;
      try {
        localStorage.setItem(`profileAvatarLocal_${user.id}`, dataUrl);
      } catch (err) {
        console.error("Could not store profile image locally (too large or storage full).", err);
        window.alert("Could not save this image. Try a smaller file, or add an avatars bucket in Supabase.");
        return;
      }
      setProfileAvatarUrl(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  // Navigation with context
  const navigateToStudy = (context = null) => {
    if (context) {
      setStudyContext(context);
      if (context.passage) {
        setStudyInput(context.passage);
      }
      if (context.sourceMode) {
        setNavigationHistory([
          ...navigationHistory,
          {
            mode: context.sourceMode,
            passage: context.passage,
            timestamp: new Date(),
          },
        ]);
      }
    }
    setActiveTab("study");
  };

  const navigateToAsk = (context = null) => {
    if (context) {
      setStudyContext(context);
      if (context.suggestedQuestions) {
        setSuggestedQuestions(context.suggestedQuestions);
      }
      if (context.sourceMode) {
        setNavigationHistory([
          ...navigationHistory,
          {
            mode: context.sourceMode,
            passage: context.passage,
            timestamp: new Date(),
          },
        ]);
      }
    }
    setActiveTab("home");
  };

  const navigateToRead = (passage = null) => {
    if (passage) {
      setReadPassage(passage);
    }
    setActiveTab("read");
  };

  // Study Generation
  const generateStudy = async () => {
    if (!studyInput.trim()) return;

    if (!checkUsageLimits("study")) {
      alert(
        `You've reached your weekly limit of ${WEEKLY_LIMITS.studies} studies. Upgrade to Premium for unlimited access!`
      );
      return;
    }

    setIsStudyLoading(true);
    updateStreak();

    try {
      const systemPrompt = `You are an expert Bible teacher. Your voice is light, friendly, encouraging, and sure—confident but never heavy. Generate a deep study of the passage that helps users understand and apply Scripture. Write in that tone: natural and approachable, like a knowledgeable friend who is both kind and certain. Do not use # or ## or hashtags anywhere. For each section title, use bold on its own line, e.g. **Passage meaning**. Avoid decorative asterisks or bullet-point overload; use clean prose.

Structure your response in this exact order:

1. Passage meaning — What does this passage say and mean? Explain clearly and accessibly; break down complex ideas simply.

2. Modern applications — How does this apply to life today? Be practical, specific, and encouraging.

3. Reflection and discussion questions — Offer 5–8 thought-provoking questions for personal reflection or group study. Make them practical and applicable.

4. Cross references — Give 3–5 related passages that illuminate this one. Format as: "Reference — brief connection."

5. Historical context — What was happening when this was written? Who wrote it, to whom, and why? Keep it engaging and relevant.

6. Original language insights — Key Hebrew or Greek words and their nuances. Keep it accessible, not overly technical, so readers appreciate the richness of the original text.

${
  studyContext?.originalQuestion
    ? `Note: This study was requested after the user asked: "${studyContext.originalQuestion}". Make connections to their question explicit.`
    : ""
}

Start each section with a bold title on its own line (e.g. **Passage meaning**), then the content. Do not use #, ##, or hashtags. Prefer flowing paragraphs over long bullet lists. Do not use stars for emphasis.`;

      const userPrompt = `The user wants a deep Bible study. Their input may be a scripture reference (e.g. "Romans 8:28" or "John 3:16-17") or the full text of a passage they pasted.

User input:
${studyInput}

Preferred Bible version: ${bibleVersion}. If the input is a reference, base the study on that passage in ${bibleVersion}. If the input is pasted passage text, study that text directly. Provide a comprehensive study following the system instructions.`;

      const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer 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",
          "Referer": window.location.origin,
          "X-Title": "Light Bible Study App",
        },
        body: JSON.stringify({
          model: "openai/gpt-4o-mini",
          max_tokens: 4000,
          messages: [
            { role: "system", content: systemPrompt },
            { role: "user", content: userPrompt }
          ],
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error?.message || `API error: ${response.status} ${response.statusText}`);
      }
      
      if (data.error) {
        throw new Error(data.error.message || "API returned an error");
      }
      
      let studyContent = data.choices?.[0]?.message?.content;
      if (!studyContent) {
        throw new Error("No content received from API");
      }
      // Remove hashtags and convert #/## lines to bold titles
      studyContent = studyContent
        .replace(/^#+\s*(.+)$/gm, (_, title) => "**" + title.replace(/\*\*/g, "").trim() + "**")
        .replace(/#/g, "");

      const study = {
        id: Date.now(),
        passage: studyInput,
        content: studyContent,
        version: bibleVersion,
        timestamp: new Date().toISOString(),
        context: studyContext,
      };

      setCurrentStudy(study);

      // Add to history
      setStudyHistory([study, ...studyHistory].slice(0, 50));

      incrementUsage("study");

      // Extract related passages for connected studies
      const relatedMatches = studyContent.match(
        /\*\*Cross-References\*\*([\s\S]*?)(?=\*\*|$)/
      );
      if (relatedMatches) {
        const relatedText = relatedMatches[1];
        const passages =
          relatedText.match(/([A-Z][a-z]+ \d+:\d+(-\d+)?)/g) || [];
        study.relatedPassages = passages.slice(0, 5);
      }
    } catch (error) {
      console.error("Study generation error:", error);
      alert("Failed to generate study. Please try again.");
    } finally {
      setIsStudyLoading(false);
      setStudyContext(null);
    }
  };

  // Ask Question
  const askQuestion = async (question = null) => {
    const finalQuestion = question || askInput;
    if (!finalQuestion.trim()) return;

    if (!checkUsageLimits("ask")) {
      alert(
        `You've reached your weekly limit of ${WEEKLY_LIMITS.asks} questions. Upgrade to Premium for unlimited access!`
      );
      return;
    }

    setIsAskLoading(true);
    updateStreak();

    try {
      const systemPrompt = `You are a Bible study companion. Your voice is light, friendly, encouraging, and sure—confident but never heavy. Guide users through Scripture with wisdom, clarity, and practical application.

Keep your responses SHORT and SWEET - aim for 2-4 concise paragraphs maximum. Sound like a helpful friend who is both kind and certain.

As a study session companion, you should:
1. Provide clear, biblically-grounded answers that are accessible and understandable (keep it brief!)
2. Reference 2-3 relevant Bible passages with brief explanations (format as "Reference - Brief connection")
3. Offer practical wisdom for applying biblical teachings to daily life (one practical takeaway is enough)
4. Stay light, friendly, encouraging, and sure in tone—never preachy or uncertain

${
  studyContext?.passage
    ? `Note: The user was reading ${studyContext.passage} when they asked this question. Reference that passage if relevant and help them connect it to their question.`
    : ""
}

Format passage references clearly (e.g., "John 3:16 - This verse shows...") so they can be easily identified and studied further.

Remember: Short, sweet, light, friendly, encouraging, and sure. Quality over length.`;

      const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer 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",
          "Referer": window.location.origin,
          "X-Title": "Light Bible Study App",
        },
        body: JSON.stringify({
          model: "openai/gpt-4o-mini",
          max_tokens: 800,
          messages: [
            { role: "system", content: systemPrompt },
            { role: "user", content: finalQuestion }
          ],
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error?.message || `API error: ${response.status} ${response.statusText}`);
      }
      
      if (data.error) {
        throw new Error(data.error.message || "API returned an error");
      }
      
      const answerContent = data.choices?.[0]?.message?.content;
      if (!answerContent) {
        throw new Error("No content received from API");
      }

      // Set question immediately so it shows while typing
      const tempAnswer = {
        id: Date.now(),
        question: finalQuestion,
        content: "",
        timestamp: new Date().toISOString(),
        context: studyContext,
      };
      setCurrentAnswer(tempAnswer);
      setPendingQuestion(finalQuestion);
      setAskInput("");
      setSuggestedQuestions([]);
      setIsAskLoading(false);

      // Extract cited passages
      const passageMatches =
        answerContent.match(/([A-Z][a-z]+ \d+:\d+(-\d+)?)/g) || [];
      const citedPassages = [...new Set(passageMatches)].slice(0, 5);

      // Start typing effect
      setIsTyping(true);
      setDisplayedText("");
      
      // Type out the response
      let currentIndex = 0;
      const typingInterval = setInterval(() => {
        if (currentIndex < answerContent.length) {
          setDisplayedText(answerContent.substring(0, currentIndex + 1));
          currentIndex++;
        } else {
          clearInterval(typingInterval);
          setIsTyping(false);
          
          // Set final answer with full content
          const finalAnswer = {
            ...tempAnswer,
            content: answerContent,
            citedPassages: citedPassages,
          };
          setCurrentAnswer(finalAnswer);
          setPendingQuestion(null);
        }
      }, 3); // Typewriter speed (lower = faster)

      incrementUsage("ask");
    } catch (error) {
      console.error("Ask error:", error);
      const errorMessage = error.message || "Failed to get answer. Please try again.";
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsAskLoading(false);
      setStudyContext(null);
    }
  };

  // Function to load a full chapter
  const loadChapter = async (bookName, chapterNum) => {
    try {
      const versionMap = {
        'KJV': 'de4e12af7f28f599-02',
        'NIV': '06125adad2d5898a-01',
        'ESV': '59f2b2a0e97d0c69-01',
        'NLT': '787ca2fa8e4d1574-01',
      };
      
      const versionId = versionMap[bibleVersion] || versionMap['NIV'];
      const bookId = normalizeBookName(bookName);
      const passageRef = `${bookName} ${chapterNum}`;
      
      // Fetch entire chapter
      const apiUrl = `https://rest.api.bible/v1/bibles/${versionId}/chapters/${bookId}.${chapterNum}`;
      
      const apiKey = process.env.REACT_APP_BIBLE_API_KEY || 'r59x1rU7Dam2-RQuIvEe9';
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'api-key': apiKey,
          'Accept': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('API Error:', response.status, errorText, 'URL:', apiUrl);
        if (response.status === 401) {
          throw new Error(`API authentication failed. Please check the API key configuration.`);
        }
        throw new Error(`Failed to fetch chapter: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      const text = data.data?.content || data.content || '';
      
      const cleanText = bibleApiHtmlToPlainText(text);
      
      const content = {
        passage: passageRef,
        text: cleanText || `Chapter ${chapterNum} of ${bookName}`,
        version: bibleVersion,
      };

      setReadContent(content);
      setIsReadingPage(true);
      setReadPassage(passageRef);
      setSelectedVerses([]);
      setShowQuickActions(false);
    } catch (error) {
      console.error("Chapter load error:", error);
      let errorMsg = error.message;
      
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorMsg = 'Network error. Please check your internet connection and try again. If the problem persists, the API may be temporarily unavailable.';
      } else if (error.message.includes('401')) {
        errorMsg = 'API authentication failed. Please check the API key configuration.';
      } else if (error.message.includes('404')) {
        errorMsg = 'Chapter not found. Please check the book name and chapter number.';
      } else if (error.message.includes('CORS')) {
        errorMsg = 'CORS error. The API may not allow requests from this domain.';
      }
      
      alert(`Failed to load chapter: ${errorMsg}`);
    }
  };

  // Helper function to normalize book names to API.Bible format
  const normalizeBookName = (bookName) => {
    const bookMap = {
      'genesis': 'GEN', 'exodus': 'EXO', 'leviticus': 'LEV', 'numbers': 'NUM',
      'deuteronomy': 'DEU', 'joshua': 'JOS', 'judges': 'JDG', 'ruth': 'RUT',
      '1 samuel': '1SA', '1st samuel': '1SA', 'first samuel': '1SA',
      '2 samuel': '2SA', '2nd samuel': '2SA', 'second samuel': '2SA',
      '1 kings': '1KI', '1st kings': '1KI', 'first kings': '1KI',
      '2 kings': '2KI', '2nd kings': '2KI', 'second kings': '2KI',
      '1 chronicles': '1CH', '1st chronicles': '1CH', 'first chronicles': '1CH',
      '2 chronicles': '2CH', '2nd chronicles': '2CH', 'second chronicles': '2CH',
      'ezra': 'EZR', 'nehemiah': 'NEH', 'esther': 'EST', 'job': 'JOB',
      'psalm': 'PSA', 'psalms': 'PSA',
      'proverbs': 'PRO', 'ecclesiastes': 'ECC', 'song of solomon': 'SNG', 'songs': 'SNG',
      'isaiah': 'ISA', 'jeremiah': 'JER', 'lamentations': 'LAM', 'ezekiel': 'EZK',
      'daniel': 'DAN', 'hosea': 'HOS', 'joel': 'JOE', 'amos': 'AMO',
      'obadiah': 'OBA', 'jonah': 'JON', 'micah': 'MIC', 'nahum': 'NAH',
      'habakkuk': 'HAB', 'zephaniah': 'ZEP', 'haggai': 'HAG', 'zechariah': 'ZEC',
      'malachi': 'MAL', 'matthew': 'MAT', 'mark': 'MRK', 'luke': 'LUK',
      'john': 'JHN', 'acts': 'ACT', 'romans': 'ROM',
      '1 corinthians': '1CO', '1st corinthians': '1CO', 'first corinthians': '1CO',
      '2 corinthians': '2CO', '2nd corinthians': '2CO', 'second corinthians': '2CO',
      'galatians': 'GAL', 'ephesians': 'EPH', 'philippians': 'PHP', 'colossians': 'COL',
      '1 thessalonians': '1TH', '1st thessalonians': '1TH', 'first thessalonians': '1TH',
      '2 thessalonians': '2TH', '2nd thessalonians': '2TH', 'second thessalonians': '2TH',
      '1 timothy': '1TI', '1st timothy': '1TI', 'first timothy': '1TI',
      '2 timothy': '2TI', '2nd timothy': '2TI', 'second timothy': '2TI',
      'titus': 'TIT', 'philemon': 'PHM', 'hebrews': 'HEB', 'james': 'JAS',
      '1 peter': '1PE', '1st peter': '1PE', 'first peter': '1PE',
      '2 peter': '2PE', '2nd peter': '2PE', 'second peter': '2PE',
      '1 john': '1JN', '1st john': '1JN', 'first john': '1JN',
      '2 john': '2JN', '2nd john': '2JN', 'second john': '2JN',
      '3 john': '3JN', '3rd john': '3JN', 'third john': '3JN',
      'jude': 'JUD', 'revelation': 'REV', 'revelations': 'REV',
    };
    
    const normalized = bookName.toLowerCase().trim();
    return bookMap[normalized] || bookName.toUpperCase().substring(0, 3);
  };

  // Quick Bible Lookup (Read mode) - Using API.Bible
  const lookupPassage = async () => {
    if (!readPassage.trim()) return;

    try {
      // Map your version codes to API.Bible version IDs
      const versionMap = {
        'KJV': 'de4e12af7f28f599-02',  // King James Version
        'NIV': '06125adad2d5898a-01',   // New International Version
        'ESV': '59f2b2a0e97d0c69-01',   // English Standard Version
        'NLT': '787ca2fa8e4d1574-01',   // New Living Translation
      };
      
      const versionId = versionMap[bibleVersion] || versionMap['NIV'];
      const passageRef = readPassage.trim();
      
      // Parse book, chapter, verse (e.g., "John 3:16" or "John 3:16-18" or "Psalm 23:1-6")
      const match = passageRef.match(/(\d?\s*[A-Za-z]+(?:\s+[A-Za-z]+)*)\s+(\d+):(\d+)(?:-(\d+))?/);
      
      if (!match) {
        // Try full chapter format (e.g., "Psalm 23" or "John 3")
        const chapterMatch = passageRef.match(/(\d?\s*[A-Za-z]+(?:\s+[A-Za-z]+)?)\s+(\d+)/);
        if (chapterMatch) {
          const [, bookName, chapter] = chapterMatch;
          const bookId = normalizeBookName(bookName);
          const parsedBook = bibleBooks.find((b) => normalizeBookName(b.name) === bookId) || null;
          const chapterNum = parseInt(chapter, 10);
          
          // Fetch entire chapter
          const apiUrl = `https://rest.api.bible/v1/bibles/${versionId}/chapters/${bookId}.${chapter}`;
          
          const apiKey = process.env.REACT_APP_BIBLE_API_KEY || 'r59x1rU7Dam2-RQuIvEe9';
          const response = await fetch(apiUrl, {
            headers: {
              'api-key': apiKey,
              'X-API-Key': apiKey,
              'Accept': 'application/json',
            },
            mode: 'cors',
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch chapter: ${response.status}`);
          }
          
          const data = await response.json();
          const text = data.data?.content || data.content || '';
          
          const cleanText = bibleApiHtmlToPlainText(text);
          
          const content = {
            passage: readPassage,
            text: cleanText || `Chapter ${chapter} of ${bookName}`,
            version: bibleVersion,
          };

          setReadContent(content);
          // Keep selectors in sync so the mobile dropdowns reflect the chosen reference.
          setSelectedBook(parsedBook);
          setCurrentTestament(parsedBook?.testament ?? "OT");
          setSelectedChapter(chapterNum);
          setSelectedVerses([]);
          setShowQuickActions(false);
          setIsReadingPage(true);
          return;
        }
        throw new Error('Invalid passage format. Use format like "John 3:16" or "John 3:16-18" or "Psalm 23"');
      }
      
      const [, bookName, chapter, startVerse, endVerse] = match;
      const bookId = normalizeBookName(bookName);
      const parsedBook = bibleBooks.find((b) => normalizeBookName(b.name) === bookId) || null;
      const chapterNum = parseInt(chapter, 10);
      const startVerseNum = parseInt(startVerse, 10);
      const endVerseNum = endVerse ? parseInt(endVerse, 10) : null;
      const verses = (() => {
        if (!startVerseNum || startVerseNum < 1) return [];
        if (endVerseNum && endVerseNum >= startVerseNum) {
          return Array.from({ length: endVerseNum - startVerseNum + 1 }, (_, i) => startVerseNum + i);
        }
        return [startVerseNum];
      })();
      
      // Build API URL for verse range
      if (endVerse) {
        // Range of verses (e.g., John 3:16-18)
        const apiUrl = `https://rest.api.bible/v1/bibles/${versionId}/passages/${bookId}.${chapter}.${startVerse}-${bookId}.${chapter}.${endVerse}`;
        
        const apiKey = process.env.REACT_APP_BIBLE_API_KEY || 'r59x1rU7Dam2-RQuIvEe9';
        const response = await fetch(apiUrl, {
          headers: {
            'api-key': apiKey,
            'X-API-Key': apiKey,
            'Accept': 'application/json',
          },
          mode: 'cors',
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch passage: ${response.status}`);
        }
        
        const data = await response.json();
        const text = data.data?.content || data.content || '';
        
        const cleanText = bibleApiHtmlToPlainText(text);
        
        const content = {
          passage: readPassage,
          text: cleanText || `Verses ${startVerse}-${endVerse} of ${bookName} ${chapter}`,
          version: bibleVersion,
        };

        setReadContent(content);
        setSelectedBook(parsedBook);
        setCurrentTestament(parsedBook?.testament ?? "OT");
        setSelectedChapter(chapterNum);
        setSelectedVerses(verses);
        setShowQuickActions(verses.length > 0);
        setIsReadingPage(true);
      } else {
        // Single verse (e.g., John 3:16)
        const apiUrl = `https://rest.api.bible/v1/bibles/${versionId}/passages/${bookId}.${chapter}.${startVerse}`;
        
        const apiKey = process.env.REACT_APP_BIBLE_API_KEY || 'r59x1rU7Dam2-RQuIvEe9';
        const response = await fetch(apiUrl, {
          headers: {
            'api-key': apiKey,
            'X-API-Key': apiKey,
            'Accept': 'application/json',
          },
          mode: 'cors',
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch verse: ${response.status}`);
        }
        
        const data = await response.json();
        const text = data.data?.content || data.content || '';
        
        const cleanText = bibleApiHtmlToPlainText(text);
        
        const content = {
          passage: readPassage,
          text: cleanText || `Verse ${startVerse} of ${bookName} ${chapter}`,
          version: bibleVersion,
        };

        setReadContent(content);
        setSelectedBook(parsedBook);
        setCurrentTestament(parsedBook?.testament ?? "OT");
        setSelectedChapter(chapterNum);
        setSelectedVerses(verses);
        setShowQuickActions(verses.length > 0);
        setIsReadingPage(true);
      }
    } catch (error) {
      console.error("Lookup error:", error);
      const errorMsg = error.message.includes('401')
        ? 'API authentication failed. Please check the API key configuration.'
        : error.message.includes('404')
        ? 'Passage not found. Please check the format (e.g., "John 3:16" or "Psalm 23:1-6").'
        : error.message;
      alert(`Failed to lookup passage: ${errorMsg}`);
    }
  };

  // Fetch a single passage for guided study session (sets guidedSessionVerseText)
  const fetchPassageForGuided = async (passageRef) => {
    const ref = (passageRef || "").trim();
    if (!ref || ref === "—") {
      setGuidedSessionVerseText(null);
      return;
    }
    setGuidedSessionVerseLoading(true);
    setGuidedSessionVerseText(null);
    try {
      const versionMap = { 'KJV': 'de4e12af7f28f599-02', 'NIV': '06125adad2d5898a-01', 'ESV': '59f2b2a0e97d0c69-01', 'NLT': '787ca2fa8e4d1574-01' };
      const versionId = versionMap[bibleVersion] || versionMap['NIV'];
      const apiKey = process.env.REACT_APP_BIBLE_API_KEY || 'r59x1rU7Dam2-RQuIvEe9';
      const match = ref.match(/(\d?\s*[A-Za-z]+(?:\s+[A-Za-z]+)*)\s+(\d+):(\d+)(?:-(\d+))?/);
      let text = "";
      if (match) {
        const [, bookName, chapter, startVerse, endVerse] = match;
        const bookId = normalizeBookName(bookName);
        const apiUrl = endVerse
          ? `https://rest.api.bible/v1/bibles/${versionId}/passages/${bookId}.${chapter}.${startVerse}-${bookId}.${chapter}.${endVerse}`
          : `https://rest.api.bible/v1/bibles/${versionId}/passages/${bookId}.${chapter}.${startVerse}`;
        const response = await fetch(apiUrl, { headers: { 'api-key': apiKey, 'Accept': 'application/json' }, mode: 'cors' });
        if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
        const data = await response.json();
        text = bibleApiHtmlToPlainText(data.data?.content || data.content || '');
      } else {
        const chapterMatch = ref.match(/(\d?\s*[A-Za-z]+(?:\s+[A-Za-z]+)?)\s+(\d+)/);
        if (chapterMatch) {
          const [, bookName, chapter] = chapterMatch;
          const bookId = normalizeBookName(bookName);
          const apiUrl = `https://rest.api.bible/v1/bibles/${versionId}/chapters/${bookId}.${chapter}`;
          const response = await fetch(apiUrl, { headers: { 'api-key': apiKey, 'Accept': 'application/json' }, mode: 'cors' });
          if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
          const data = await response.json();
          text = bibleApiHtmlToPlainText(data.data?.content || data.content || '');
        }
      }
      setGuidedSessionVerseText(text || `Passage: ${ref}`);
    } catch (err) {
      console.error("Guided passage fetch:", err);
      setGuidedSessionVerseText(`Could not load "${ref}". Check format (e.g. Mark 12:30 or Psalm 103:11-14).`);
    } finally {
      setGuidedSessionVerseLoading(false);
    }
  };

  const toggleVerseSelection = (verseNum) => {
    setSelectedVerses((prev) => {
      const newSelection = prev.includes(verseNum)
        ? prev.filter((v) => v !== verseNum)
        : [...prev, verseNum].sort((a, b) => a - b);
      setShowQuickActions(newSelection.length > 0);
      return newSelection;
    });
  };

  const studyFromRead = () => {
    const context = {
      sourceMode: "read",
      passage: `${readPassage}${
        selectedVerses.length > 0
          ? ` (verses ${selectedVerses.join(", ")})`
          : ""
      }`,
      bibleText: readContent?.text,
    };
    navigateToStudy(context);
  };

  const askFromRead = () => {
    const context = {
      sourceMode: "read",
      passage: readPassage,
      bibleText: readContent?.text,
      suggestedQuestions: [
        `What does ${readPassage} mean?`,
        `How does ${readPassage} apply to my life?`,
        `What is the context of ${readPassage}?`,
      ],
    };
    navigateToAsk(context);
  };

  const processPassageTextSelection = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      return;
    }
    const range = sel.getRangeAt(0);
    const startN = range.startContainer;
    const inBibleTab =
      bibleTabPassageRef.current &&
      (bibleTabPassageRef.current.contains(startN) ||
        (startN.nodeType === Node.TEXT_NODE &&
          bibleTabPassageRef.current.contains(startN.parentElement)));
    const inHomeReading =
      homeReadingPassageRef.current &&
      (homeReadingPassageRef.current.contains(startN) ||
        (startN.nodeType === Node.TEXT_NODE &&
          homeReadingPassageRef.current.contains(startN.parentElement)));
    if (!inBibleTab && !inHomeReading) {
      return;
    }

    /** Signed-in Study chapter reader: partial verse highlight (keep selection until color chosen). */
    if (
      inHomeReading &&
      activeTab === "study" &&
      isReadingPage &&
      user &&
      readContent?.passage
    ) {
      const root = homeReadingPassageRef.current;
      const segments = root ? getOffsetsForStudySelection(range, root) : [];
      const raw = sel.toString();
      if (segments.length >= 1 && raw.trim().length >= 1) {
        setVerseHighlightMenu({
          segments,
          anchorRect: range.getBoundingClientRect(),
        });
        return;
      }
    }

    const text = sel.toString().trim();
    if (text.length < 3) {
      return;
    }
    if (!readContent?.passage) {
      return;
    }

    if (inBibleTab && bibleTabPassageRef.current && readContent.text) {
      const resolved = resolveHighlightOffsets(
        bibleTabPassageRef.current,
        range,
        readContent.text
      );
      if (resolved && resolved.end - resolved.start >= 3) {
        setPassageHighlights((prev) =>
          mergeHighlightRanges(prev, resolved.start, resolved.end)
        );
      }
    }

    window.getSelection()?.removeAllRanges();
  }, [readContent, activeTab, isReadingPage, user]);

  const handlePassageTextSelect = useCallback(() => {
    requestAnimationFrame(() => {
      processPassageTextSelection();
    });
  }, [processPassageTextSelection]);

  const handleVerseHighlightColor = useCallback(
    async (color, menu) => {
      if (!menu || !user || !selectedBook || selectedChapter == null) return;
      const segments = menu.segments;
      if (!segments?.length) return;
      setVerseHighlightMenu(null);
      window.getSelection()?.removeAllRanges();
      const base = Date.now();
      const tempIds = segments.map((_, i) => `optimistic-${base}-${i}`);
      setChapterVerseHighlights((m) => {
        const next = { ...m };
        segments.forEach((seg, i) => {
          const prev = next[seg.verseNum] || [];
          const nextRow = {
            id: tempIds[i],
            start_offset: seg.startOffset,
            end_offset: seg.endOffset,
            color,
          };
          next[seg.verseNum] = [...prev, nextRow].sort(
            (a, b) => a.start_offset - b.start_offset || a.end_offset - b.end_offset
          );
        });
        return next;
      });
      try {
        const rows = await Promise.all(
          segments.map((seg) =>
            insertTextHighlight(supabase, user.id, {
              book: selectedBook.name,
              chapter: selectedChapter,
              verse: seg.verseNum,
              start_offset: seg.startOffset,
              end_offset: seg.endOffset,
              color,
            })
          )
        );
        setChapterVerseHighlights((m) => {
          const next = { ...m };
          segments.forEach((seg, i) => {
            const row = rows[i];
            const list = (next[seg.verseNum] || []).map((x) =>
              x.id === tempIds[i]
                ? {
                    id: row.id,
                    start_offset: row.start_offset,
                    end_offset: row.end_offset,
                    color: row.color,
                  }
                : x
            );
            next[seg.verseNum] = list;
          });
          return next;
        });
      } catch (err) {
        console.error("verse text highlight insert", err);
        alert("Could not save highlight. Please try again.");
        setChapterVerseHighlights((m) => {
          const next = { ...m };
          segments.forEach((seg, i) => {
            const list = (next[seg.verseNum] || []).filter((x) => x.id !== tempIds[i]);
            if (list.length) next[seg.verseNum] = list;
            else delete next[seg.verseNum];
          });
          return next;
        });
        try {
          const map = await fetchChapterTextHighlights(supabase, {
            book: selectedBook.name,
            chapter: selectedChapter,
          });
          setChapterVerseHighlights(map);
        } catch (_) {
          setChapterVerseHighlights({});
        }
      }
    },
    [user, selectedBook, selectedChapter, supabase]
  );

  const handleVerseHighlightRemove = useCallback(
    async (menu) => {
      if (!menu || !user || !selectedBook || selectedChapter == null) return;
      const segments = menu.segments;
      if (!segments?.length) return;
      setVerseHighlightMenu(null);
      window.getSelection()?.removeAllRanges();
      setChapterVerseHighlights((m) => {
        const next = { ...m };
        segments.forEach((seg) => {
          const list = next[seg.verseNum] || [];
          const filtered = list.filter(
            (h) => !(h.start_offset < seg.endOffset && h.end_offset > seg.startOffset)
          );
          if (filtered.length) next[seg.verseNum] = filtered;
          else delete next[seg.verseNum];
        });
        return next;
      });
      try {
        await Promise.all(
          segments.map((seg) =>
            deleteOverlappingTextHighlights(supabase, {
              book: selectedBook.name,
              chapter: selectedChapter,
              verse: seg.verseNum,
              selStart: seg.startOffset,
              selEnd: seg.endOffset,
            })
          )
        );
      } catch (err) {
        console.error("verse text highlight delete", err);
        alert("Could not remove highlight.");
        try {
          const map = await fetchChapterTextHighlights(supabase, {
            book: selectedBook.name,
            chapter: selectedChapter,
          });
          setChapterVerseHighlights(map);
        } catch (_) {}
      }
    },
    [user, selectedBook, selectedChapter, supabase]
  );

  /** Load persisted verse highlights when the Study chapter reader is open. */
  useEffect(() => {
    if (
      !user?.id ||
      !selectedBook?.name ||
      selectedChapter == null ||
      !readContent?.text ||
      !isReadingPage
    ) {
      setChapterVerseHighlights({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const map = await fetchChapterTextHighlights(supabase, {
          book: selectedBook.name,
          chapter: selectedChapter,
        });
        if (!cancelled) setChapterVerseHighlights(map);
      } catch (err) {
        console.error("chapter highlights load", err);
        if (!cancelled) setChapterVerseHighlights({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, selectedBook?.name, selectedChapter, readContent?.passage, isReadingPage, readContent?.text]);

  useEffect(() => {
    setVerseHighlightMenu(null);
  }, [readContent?.passage, isReadingPage]);

  useEffect(() => {
    setPassageHighlights([]);
  }, [readContent?.passage, readContent?.text]);

  useEffect(() => {
    const onScroll = () => {
      setVerseHighlightMenu(null);
    };
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setVerseHighlightMenu(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Save functions
  const saveStudy = (study) => {
    if (!savedStudies.find((s) => s.id === study.id)) {
      setSavedStudies([study, ...savedStudies]);
    }
  };

  const saveAnswer = (answer) => {
    if (!savedAnswers.find((a) => a.id === answer.id)) {
      setSavedAnswers([answer, ...savedAnswers]);
      // Show confirmation
      alert("Answer saved! You can view it in the Library under 'Saved Answers'.");
    } else {
      alert("This answer is already saved.");
    }
  };

  // Notes functions
  const createNote = () => {
    if (!noteTitle.trim()) return;

    const note = {
      id: Date.now(),
      title: noteTitle,
      content: noteContent,
      tags: noteTags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t),
      timestamp: new Date().toISOString(),
      lastModified: new Date().toISOString(),
      linkedPassage: studyContext?.passage || null,
    };

    setNotes([note, ...notes]);
    resetNoteEditor();
  };

  const updateNote = () => {
    setNotes(
      notes.map((note) =>
        note.id === currentNote.id
          ? {
              ...note,
              title: noteTitle,
              content: noteContent,
              tags: noteTags
                .split(",")
                .map((t) => t.trim())
                .filter((t) => t),
              lastModified: new Date().toISOString(),
            }
          : note
      )
    );
    resetNoteEditor();
  };

  const editNote = (note) => {
    setCurrentNote(note);
    setNoteTitle(note.title);
    setNoteContent(note.content);
    setNoteTags(note.tags.join(", "));
    setShowNoteEditor(true);
  };

  const deleteNote = (noteId) => {
    if (window.confirm("Delete this note?")) {
      setNotes(notes.filter((note) => note.id !== noteId));
    }
  };

  const resetNoteEditor = () => {
    setShowNoteEditor(false);
    setCurrentNote(null);
    setNoteTitle("");
    setNoteContent("");
    setNoteTags("");
  };

  const filteredNotes = notes.filter(
    (note) =>
      note.title.toLowerCase().includes(noteSearch.toLowerCase()) ||
      note.content.toLowerCase().includes(noteSearch.toLowerCase()) ||
      note.tags.some((tag) =>
        tag.toLowerCase().includes(noteSearch.toLowerCase())
      )
  );

  // Render helpers
  const renderBreadcrumbs = () => {
    if (navigationHistory.length === 0) return null;

    return (
      <div className="mb-6 flex items-center gap-3 text-sm overflow-x-auto">
        {navigationHistory.slice(-3).map((nav, idx) => (
          <span key={idx} className="flex items-center gap-2 text-[#1E293B]">
            <span className="whitespace-nowrap">
              {nav.mode === "read" ? "📖" : nav.mode === "ask" ? "💭" : "📚"}{" "}
              {nav.passage}
            </span>
            {idx < navigationHistory.slice(-3).length - 1 && <span className="text-[#64748B]">→</span>}
          </span>
        ))}
        <button
          onClick={() => setNavigationHistory([])}
          className="ml-2 text-xs text-[#64748B] hover:text-[#475569] transition-colors"
        >
          Clear
        </button>
      </div>
    );
  };

  /** iPhone/small screens: Bible version as a glass dropdown with cover thumbnails */
  const renderBibleVersionPickerPhone = (opts = {}) => {
    const { triggerClassName = "" } = opts;
    const current =
      bibleVersions.find((v) => v.value === bibleVersion) || bibleVersions[0];

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex w-full items-center gap-3 rounded-xl border border-[#3B82F6]/35 bg-[#E2E8F0]/25 px-3 py-2.5 text-left font-semibold text-[#1E293B] shadow-sm transition-colors hover:bg-[#E2E8F0]/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e2c275]/50",
              triggerClassName
            )}
          >
            <img
              src={current.image}
              alt=""
              className="h-9 w-9 shrink-0 rounded-lg border border-[#3B82F6]/25 object-cover"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <span className="min-w-0 flex-1 truncate">{current.value}</span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="center"
          sideOffset={8}
          className="z-[60] max-h-[min(70vh,22rem)] w-[min(calc(100vw-2rem),20rem)] overflow-y-auto rounded-2xl border border-[#3B82F6]/30 bg-[rgba(248,250,252,0.96)] p-2 text-[#1E293B] shadow-xl backdrop-blur-xl data-[state=open]:animate-in data-[state=closed]:animate-out"
        >
          {bibleVersions.map((v) => {
            const locked = v.premium && !isPremium;
            return (
              <DropdownMenuItem
                key={v.value}
                disabled={locked}
                className="gap-3 rounded-xl px-3 py-2.5 text-[#1E293B] hover:bg-[#E2E8F0]/90 focus:bg-[#E2E8F0]/90"
                onSelect={() => {
                  if (!locked) setBibleVersion(v.value);
                }}
              >
                <img
                  src={v.image}
                  alt=""
                  className="h-10 w-10 shrink-0 rounded-lg border border-[#3B82F6]/20 object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 font-semibold">
                    {v.value}
                    {v.premium && (
                      <span className="text-[10px] uppercase tracking-wider text-[#e2c275]">
                        Premium
                      </span>
                    )}
                  </div>
                  <div className="truncate text-xs font-medium text-[#64748B]">
                    {v.label}
                  </div>
                </div>
                {bibleVersion === v.value && !locked && (
                  <Check className="h-4 w-4 shrink-0 text-[#e2c275]" aria-hidden />
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderUsageLimits = () => {
    return null; // Free tier UI disabled for now — unlimited access
  };

  const renderGuidedPlanDetail = () => {
    if (!activeGuidedPlan) return null;

    const close = () => setActiveGuidedPlan(null);

    const isWorship = activeGuidedPlan.id === "worship_priority";

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4 md:px-6">
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={close}
        />
        <div className="relative max-w-4xl w-full max-h-[85vh] overflow-y-auto glass-card rounded-3xl border border-[#3B82F6]/30 bg-white shadow-[0_24px_80px_rgba(148,163,184,0.45)] p-6 md:p-10 text-[#1E293B]">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <p className="gold-text text-xs font-bold uppercase tracking-wider mb-2">
                Guided Study Overview
              </p>
              <h2 className="serif text-3xl md:text-4xl font-bold text-[#1E293B] mb-2">
                {activeGuidedPlan.title || "Guided Study Plan"}
              </h2>
              {activeGuidedPlan.days && (
                <p className="text-sm text-[#475569]">
                  {activeGuidedPlan.days}-day guided journey
                </p>
              )}
            </div>
            <button
              onClick={close}
              className="shrink-0 px-3 py-1.5 text-xs uppercase tracking-wider font-semibold rounded-full border border-[#3B82F6]/30 text-[#1E293B] hover:bg-[#E2E8F0]/80 transition-all"
            >
              Close
            </button>
          </div>

          <div className="space-y-8 text-sm md:text-base leading-relaxed text-[#475569]">
            {isWorship ? (
              <>
                <section>
                  <h3 className="serif text-xl md:text-2xl font-semibold text-[#1E293B] mb-3">
                    About this study
                  </h3>
                  <p className="mb-3">
                    If worship only happens when you choose to pray, when you go to church, when
                    you “feel” the Holy Spirit, or when you’re singing a song—then we have to ask:
                    is worship actually first?
                  </p>
                  <p className="mb-3">
                    Worship is not an event. It is not a mood. It is not a Sunday moment. It is a
                    posture.
                  </p>
                  <p>
                    So the question becomes: how do we move worship to the top of our lives? This
                    5-day journey walks through that question together.
                  </p>
                </section>

                <section>
                  <h3 className="serif text-xl md:text-2xl font-semibold text-[#1E293B] mb-4">
                    The Learning Arc
                  </h3>
                  <div className="relative pl-12">
                    {/* Vertical line through center of dots */}
                    <div className="absolute left-5 top-0 bottom-0 w-px bg-[#e2c275]/60" style={{ marginLeft: '-1px' }} />
                    {/* Cross in the middle of the line */}
                    <span className="absolute left-5 top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-white border border-[#e2c275]/50 flex items-center justify-center text-[#e2c275] text-sm font-bold shadow-sm" style={{ marginLeft: '-1px' }}>✝</span>
                    <div className="space-y-6">
                      <div className="flex gap-4 items-start">
                        <div className="flex justify-center shrink-0 w-10 h-10 rounded-full border-2 border-[#e2c275]/60 bg-transparent flex items-center justify-center text-lg shadow-[0_0_14px_rgba(226,194,117,0.35)]">
                          🙏
                        </div>
                        <div>
                          <p className="font-semibold text-[#1E293B]">Reverence — The Foundation of Worship</p>
                          <p className="text-sm text-[#64748B] mt-0.5">Days 1–2</p>
                        </div>
                      </div>
                      <div className="flex gap-4 items-start">
                        <div className="flex justify-center shrink-0 w-10 h-10 rounded-full border-2 border-[#e2c275]/60 bg-transparent flex items-center justify-center text-lg shadow-[0_0_14px_rgba(226,194,117,0.35)]">
                          ⏳
                        </div>
                        <div>
                          <p className="font-semibold text-[#1E293B]">The Reality of Time</p>
                          <p className="text-sm text-[#64748B] mt-0.5">Days 2–3</p>
                        </div>
                      </div>
                      <div className="flex gap-4 items-start">
                        <div className="flex justify-center shrink-0 w-10 h-10 rounded-full border-2 border-[#e2c275]/60 bg-transparent flex items-center justify-center text-lg shadow-[0_0_14px_rgba(226,194,117,0.35)]">
                          ⚖️
                        </div>
                        <div>
                          <p className="font-semibold text-[#1E293B]">The Power of Yes and No</p>
                          <p className="text-sm text-[#64748B] mt-0.5">Days 4–5</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <h3 className="serif text-xl md:text-2xl font-semibold text-[#1E293B] mt-8 mb-3">
                    Each day includes
                  </h3>
                  <p className="text-[#64748B] text-sm mb-4">~15–20 minutes per session</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="flex flex-col items-center text-center p-4 rounded-2xl border border-[#3B82F6]/25 bg-[#E2E8F0]/30">
                      <div className="w-10 h-10 rounded-xl border-2 border-[#e2c275]/60 bg-transparent flex items-center justify-center mb-2 shadow-[0_0_14px_rgba(226,194,117,0.35)]">
                        <BookOpen className="w-5 h-5 text-[#e2c275]" />
                      </div>
                      <p className="font-semibold text-[#1E293B] text-sm">Scripture passage</p>
                      <p className="text-xs text-[#64748B] mt-0.5">Focused reading</p>
                    </div>
                    <div className="flex flex-col items-center text-center p-4 rounded-2xl border border-[#3B82F6]/25 bg-[#E2E8F0]/30">
                      <div className="w-10 h-10 rounded-xl border-2 border-[#e2c275]/60 bg-transparent flex items-center justify-center mb-2 shadow-[0_0_14px_rgba(226,194,117,0.35)]">
                        <MessageCircle className="w-5 h-5 text-[#e2c275]" />
                      </div>
                      <p className="font-semibold text-[#1E293B] text-sm">Guided reflection</p>
                      <p className="text-xs text-[#64748B] mt-0.5">Prompted questions</p>
                    </div>
                    <div className="flex flex-col items-center text-center p-4 rounded-2xl border border-[#3B82F6]/25 bg-[#E2E8F0]/30">
                      <div className="w-10 h-10 rounded-xl border-2 border-[#e2c275]/60 bg-transparent flex items-center justify-center mb-2 shadow-[0_0_14px_rgba(226,194,117,0.35)]">
                        <Sparkles className="w-5 h-5 text-[#e2c275]" />
                      </div>
                      <p className="font-semibold text-[#1E293B] text-sm">Personal activation</p>
                      <p className="text-xs text-[#64748B] mt-0.5">Apply what you learn</p>
                    </div>
                  </div>
                </section>
              </>
            ) : (
              <section>
                <h3 className="serif text-xl md:text-2xl font-semibold text-[#1E293B] mb-3">
                  About this study
                </h3>
                <p className="mb-4 text-[#475569]">
                  {activeGuidedPlan.blurb}
                </p>
                {activeGuidedPlan.reference && (
                  <p className="text-[#64748B] text-sm">
                    Key passages: <span className="font-medium text-[#1E293B]">{activeGuidedPlan.reference}</span>
                  </p>
                )}
                <h3 className="serif text-xl md:text-2xl font-semibold text-[#1E293B] mt-6 mb-3">
                  The learning arc
                </h3>
                <p className="text-[#64748B] text-sm">
                  Daily content and section titles will appear when you start the plan.
                </p>
              </section>
            )}
          </div>
          <div className="mt-8 pt-6 border-t border-[#E2E8F0] flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
            <p className="text-xs sm:text-sm text-[#64748B]">
              {activePlanProgress?.planId === activeGuidedPlan.id
                ? "Pick up where you left off and continue your journey."
                : "When you're ready, start this plan and walk through each day at your own pace."}
            </p>
            <button
              onClick={() => {
                if (activePlanProgress?.planId === activeGuidedPlan.id) {
                  setGuidedView("in_plan");
                  setActiveGuidedPlan(null);
                } else {
                  setActivePlanProgress({
                    planId: activeGuidedPlan.id,
                    planTitle: activeGuidedPlan.title,
                    totalDays: activeGuidedPlan.days || 5,
                    currentDay: 1,
                    completedDays: [],
                  });
                  setGuidedView("in_plan");
                  setActiveGuidedPlan(null);
                }
              }}
              className="px-6 py-3 bg-[#e2c275] text-black rounded-2xl hover:bg-[#e2c275]/90 transition-all text-xs sm:text-sm font-semibold uppercase tracking-wider"
            >
              {activePlanProgress?.planId === activeGuidedPlan.id ? "Continue" : "Start This Plan"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Show loading screen while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#1E293B]">Loading...</div>
      </div>
    );
  }

  return (
    <div ref={appShellRef} className="min-h-screen relative w-full max-w-[100vw] min-w-0 overflow-x-hidden">
      {/* Guided Plan Detail Overlay */}
      {renderGuidedPlanDetail()}

      {/* Auth Modal */}
      {showAuth && (
        <Auth 
          onAuthSuccess={handleAuthSuccess} 
          onClose={() => {
            setShowAuth(false);
            setAuthMode('login'); // Reset to login mode when closing
          }}
          initialMode={authMode}
        />
      )}

      {showPasswordReset && (
        <PasswordResetModal
          onClose={() => {
            passwordRecoveryActiveRef.current = false;
            setShowPasswordReset(false);
          }}
        />
      )}

      {/* Name Prompt Modal */}
      {showNamePrompt && (
        <div className="auth-modal-root fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
          <div className="glass-card rounded-2xl border border-[#3B82F6]/30 p-8 w-full max-w-md relative">
            <div className="mb-6">
              <h2 className="serif text-3xl font-bold text-[#1E293B] mb-2">
                Welcome!
              </h2>
              <p className="text-sm text-[#475569]">
                Let's get to know you better. What's your first name?
              </p>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const firstName = formData.get('firstName');
              handleNameSubmit(firstName);
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#475569] mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  placeholder="Enter your first name"
                  required
                  autoFocus
                  className="w-full px-4 py-3 glass-card border border-[#3B82F6]/30 rounded-xl focus:outline-none focus:border-[#e2c275]/50 text-[#1E293B] placeholder:text-[#64748B] bg-[#E2E8F0]/30"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-[#e2c275] text-black font-semibold rounded-xl hover:bg-[#e2c275]/90 transition-all uppercase tracking-wider text-sm"
              >
                Continue
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Ambient Background */}
      <div className="ambient-bg">
        <div className="ambient-glow" style={{top: 0, left: '10%'}}></div>
        <div className="ambient-glow" style={{bottom: 0, right: '5%', animationDelay: '-7s'}}></div>
      </div>

      {/* Navigation — safe-area band stays put; nav row (logo, links, auth) hides on scroll down. */}
      {createPortal(
        <nav
          ref={navRef}
          className="top-nav-fixed overflow-hidden"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 40,
            width: "100vw",
            maxWidth: "none",
            margin: 0,
            padding: 0,
            marginLeft: 0,
            paddingLeft: 0,
            pointerEvents: "none",
          }}
        >
          <div
            className="top-nav-safe-band"
            style={{ height: "env(safe-area-inset-top, 0px)" }}
            aria-hidden="true"
          />
          <div
            className="nav-glass px-4 sm:px-6 md:px-8 lg:px-10 py-4 md:py-5 flex justify-between items-center shadow-2xl min-w-0 overflow-x-hidden box-border w-full"
            style={{
              transform: navBarVisible ? "translateY(0)" : "translateY(-100%)",
              transition: "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
              pointerEvents: navBarVisible ? "auto" : "none",
            }}
          >
            {/* Left Section - Logo */}
            <div className="flex items-center flex-1 min-w-0 shrink">
              <a 
                href="#" 
                onClick={(e) => { 
                  e.preventDefault(); 
                  setActiveTab("home");
                }} 
                className="flex items-center gap-2 group"
              >
                <h1 className="text-xl font-light tracking-widest uppercase text-[#1E293B]">Ligh<span className="logo-cross text-[#e2c275] text-2xl">✝</span></h1>
              </a>
            </div>

            {/* Center Section - Navigation Links */}
            <div className="hidden md:flex items-center gap-10 text-sm uppercase tracking-widest font-semibold text-[#475569] flex-1 justify-center" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <button 
                onClick={() => setActiveTab("home")}
                className={`nav-link transition-colors ${activeTab === "home" ? "text-[#1E293B]" : "text-[#475569] hover:text-[#1E293B]"}`}
              >
                Home
              </button>
              <button 
                onClick={() => setActiveTab("study")}
                className={`nav-link transition-colors ${activeTab === "study" ? "text-[#1E293B]" : "text-[#475569] hover:text-[#1E293B]"}`}
              >
                Deep Study
              </button>
              <button 
                onClick={() => setActiveTab("library")}
                className={`nav-link transition-colors ${activeTab === "library" ? "text-[#1E293B]" : "text-[#475569] hover:text-[#1E293B]"}`}
              >
                Library
              </button>
              <button 
                onClick={() => { setActiveTab("guided"); setGuidedView("plans"); }}
                className={`nav-link transition-colors ${activeTab === "guided" ? "text-[#1E293B]" : "text-[#475569] hover:text-[#1E293B]"}`}
              >
                Guided Study
              </button>
              <button 
                onClick={() => setActiveTab("profile")}
                className={`nav-link transition-colors ${activeTab === "profile" ? "text-[#1E293B]" : "text-[#475569] hover:text-[#1E293B]"}`}
              >
                Profile
              </button>
            </div>
            <div className="flex items-center gap-4 flex-1 justify-end min-w-0 shrink">
              {user ? (
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setDarkMode((d) => !d)}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#3B82F6]/30 bg-[#E2E8F0]/25 text-[#1E293B] transition-colors hover:bg-[#E2E8F0]/45 dark:border-[#60a5fa]/35 dark:bg-[#1e3a5f]/60 dark:text-[#f1f5f9] dark:hover:bg-[#1e3a5f]/90"
                      aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                      title={darkMode ? "Light mode" : "Dark mode"}
                    >
                      {darkMode ? (
                        <Sun className="h-[18px] w-[18px] text-[#e2c275]" strokeWidth={2.25} />
                      ) : (
                        <Moon className="h-[18px] w-[18px]" strokeWidth={2.25} />
                      )}
                    </button>
                    <div
                      className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#3B82F6]/30 bg-[#e2c275]/15 ring-1 ring-white/40 dark:border-[#60a5fa]/35 dark:bg-[#e2c275]/10 dark:ring-white/10"
                      title={user.email || "Profile"}
                    >
                      {profileAvatarUrl ? (
                        <img src={profileAvatarUrl} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <User className="h-4 w-4 text-[#475569] dark:text-[#94a3b8]" />
                      )}
                    </div>
                    <span className="min-w-0 max-w-[5.5rem] truncate text-left text-[10px] font-semibold leading-tight text-[#1E293B] dark:text-[#e2e8f0]">
                      {userFirstName || user.email?.split("@")[0] || "Account"}
                    </span>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-2 px-4 py-2 glass-card border border-[#3B82F6]/30 rounded-xl hover:bg-[#E2E8F0]/30 transition-all text-sm text-[#1E293B] font-medium dark:hover:bg-[#1e3a5f]/50"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden sm:inline">Sign Out</span>
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setDarkMode((d) => !d)}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#3B82F6]/30 bg-[#E2E8F0]/25 text-[#1E293B] transition-colors hover:bg-[#E2E8F0]/45 dark:border-[#60a5fa]/35 dark:bg-[#1e3a5f]/60 dark:text-[#f1f5f9] dark:hover:bg-[#1e3a5f]/90"
                    aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                    title={darkMode ? "Light mode" : "Dark mode"}
                  >
                    {darkMode ? (
                      <Sun className="h-[18px] w-[18px] text-[#e2c275]" strokeWidth={2.25} />
                    ) : (
                      <Moon className="h-[18px] w-[18px]" strokeWidth={2.25} />
                    )}
                  </button>
                  <button
                    onClick={() => setShowAuth(true)}
                    className="hidden sm:block text-[10px] uppercase tracking-widest font-bold border border-[#3B82F6]/30 px-6 py-2.5 rounded-full hover:bg-[#E2E8F0]/30 transition-all text-[#1E293B]"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => {
                      setAuthMode('signup');
                      setShowAuth(true);
                    }}
                    className="bg-[#e2c275] text-black text-[10px] uppercase tracking-widest font-black px-6 py-2.5 rounded-full hover:bg-[#e2c275]/90 hover:shadow-[0_4px_20px_rgba(226,194,117,0.4)] transition-all"
                  >
                    Get Started
                  </button>
                </>
              )}
            </div>
          </div>
      </nav>,
        document.body
      )}

      {verseHighlightMenu && user && (
        <VerseHighlightMenu
          anchorRect={verseHighlightMenu.anchorRect}
          onSelectColor={(c) => handleVerseHighlightColor(c, verseHighlightMenu)}
          onRemove={() => handleVerseHighlightRemove(verseHighlightMenu)}
          onClose={() => {
            window.getSelection()?.removeAllRanges();
            setVerseHighlightMenu(null);
          }}
        />
      )}

      <main
        ref={mainContentRef}
        className="max-w-7xl mx-auto px-4 md:px-8 pt-20 md:pt-24 pb-[calc(4rem+env(safe-area-inset-bottom,0px)+0.5rem)] md:pb-20 relative z-10 w-full overflow-x-hidden"
      >
        <div
          ref={tabTopAnchorRef}
          tabIndex={-1}
          aria-hidden="true"
          className="absolute left-0 top-0 h-px w-px overflow-hidden opacity-0 pointer-events-none"
        />
        <div ref={tabContentRef} className="gsap-tab-content min-h-[600px] text-[#1E293B]">
          {renderBreadcrumbs()}
          {renderUsageLimits()}

          {/* STUDY TAB */}
          {activeTab === "study" && (
            <div>
              <div className="mb-8">
                <h2 className="serif text-4xl font-bold text-[#1E293B] mb-3">
                  Deep Study
                </h2>
                <p className="text-[#475569] text-base">
                  Get comprehensive insights, context, and discussion questions
                  for any passage
                </p>
              </div>

              {/* Bible passage search — top of Study tab, under intro */}
              <div className="mb-8">
                <label className="block gold-text text-xs font-bold uppercase tracking-wider mb-2">
                  Find a passage
                </label>
                <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                  <input
                    type="text"
                    value={bibleSearch}
                    onChange={(e) => setBibleSearch(e.target.value)}
                    placeholder="Search book, chapter, or verse (e.g. John 3:16)"
                    className="flex-1 min-w-0 p-4 glass-card border border-[#3B82F6]/30 rounded-2xl focus:outline-none focus:border-[#e2c275]/50 text-[#1E293B] placeholder:text-[#64748B] bg-[#E2E8F0]/30"
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && bibleSearch.trim()) {
                        setReadPassage(bibleSearch);
                        lookupPassage();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (bibleSearch.trim()) {
                        setReadPassage(bibleSearch);
                        lookupPassage();
                      }
                    }}
                    className="shrink-0 px-8 py-4 bg-[#e2c275] text-black font-semibold rounded-2xl hover:bg-[#e2c275]/90 transition-all flex items-center justify-center gap-2 uppercase tracking-wider text-sm sm:px-8"
                  >
                    <Search className="w-5 h-5" />
                    Search
                  </button>
                </div>
              </div>

              {/* Bible Version Library */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-[#1E293B] mb-4">Bible Version</h3>
                <div className="mb-4 md:hidden">{renderBibleVersionPickerPhone()}</div>
                <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4">
                    {bibleVersions.map((version) => (
                    <button
                      key={version.value}
                      onClick={() => setBibleVersion(version.value)}
                      className={`glass-card rounded-xl md:rounded-2xl border overflow-hidden transition-all cursor-pointer ${
                        bibleVersion === version.value
                          ? 'border-[#e2c275] bg-[#e2c275]/10'
                          : 'border-[#3B82F6]/30 hover:border-[#e2c275]/30 hover:bg-[#E2E8F0]/30'
                      }`}
                    >
                      <div className="aspect-square md:aspect-[2/3] w-full overflow-hidden bg-[#E2E8F0]/30">
                        <img 
                          src={version.image} 
                          alt={version.label}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-full bg-gradient-to-br from-[#e2c275]/20 to-transparent flex items-center justify-center text-[#475569] font-bold text-lg md:text-2xl" style={{ display: 'none' }}>
                          {version.value}
                        </div>
                      </div>
                      <div className="p-2 md:p-4">
                        <div className="font-semibold text-[#1E293B] text-xs md:text-base mb-0.5 md:mb-1">{version.value}</div>
                        <div className="text-[10px] md:text-xs text-[#475569] mb-0 md:mb-2 hidden md:block">{version.label}</div>
                        {version.premium && (
                          <div className="text-[10px] md:text-xs text-[#e2c275] uppercase tracking-wider">Premium</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Bible Reading Section */}
              {isReadingPage && readContent ? (
                <div
                  ref={bibleReaderTopRef}
                  className="min-h-screen scroll-mt-20 md:scroll-mt-24"
                >
                  {/* Back Button - Outside bubble */}
                  <div className="mb-6">
                    <button
                      onClick={() => {
                        setIsReadingPage(false);
                        setReadContent(null);
                      }}
                      className="px-4 py-2 glass-card border border-[#3B82F6]/30 rounded-xl hover:bg-[#E2E8F0]/30 transition-all text-sm text-[#1E293B] font-medium flex items-center gap-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to Books
                    </button>
                  </div>

                  {/* Full chapter text; bottom buttons load previous/next chapter and scroll to top */}
                  {(() => {
                    const pageText = readContent.text;

                    const canGoToPreviousChapter = selectedChapter > 1;
                    const canGoToNextChapter =
                      selectedBook && selectedChapter < selectedBook.chapters;

                    const handlePrevChapter = () => {
                      if (!canGoToPreviousChapter || !selectedBook) return;
                      const prevChapter = selectedChapter - 1;
                      setSelectedChapter(prevChapter);
                      loadChapter(selectedBook.name, prevChapter);
                    };

                    const handleNextChapter = () => {
                      if (!canGoToNextChapter || !selectedBook) return;
                      const nextChapter = selectedChapter + 1;
                      setSelectedChapter(nextChapter);
                      loadChapter(selectedBook.name, nextChapter);
                    };

                    const prevDisabled = !canGoToPreviousChapter;
                    const nextDisabled = !canGoToNextChapter;

                    const prevNavLabel =
                      !prevDisabled && selectedBook
                        ? `${selectedBook.name} ${selectedChapter - 1}`
                        : "—";
                    const nextNavLabel =
                      !nextDisabled && selectedBook
                        ? `${selectedBook.name} ${selectedChapter + 1}`
                        : "—";

                    return (
                      <div className="min-h-[calc(100vh-300px)]">
                        {/* Single Page */}
                        <div className="glass-card rounded-3xl border border-[#3B82F6]/30 p-12 bg-[#E2E8F0]/10 mx-auto max-w-4xl relative">
                          {/* Header inside bubble - Top Left */}
                          <div className="absolute top-6 left-6">
                            <h2 className="serif text-2xl font-bold text-[#1E293B]">
                              {readContent.passage}
                            </h2>
                            <p className="text-sm text-[#475569] mt-1">
                              {readContent.version}
                            </p>
                          </div>
                          
                          {/* Buttons inside bubble - Top Right */}
                          <div className="absolute top-6 right-6 flex gap-2">
                            <button
                              onClick={() => {
                                setStudyInput(readContent.passage);
                                setIsReadingPage(false);
                              }}
                              className="px-4 py-2 glass-card border border-[#3B82F6]/30 rounded-xl hover:bg-[#e2c275]/10 hover:border-[#e2c275] transition-all text-sm text-[#1E293B] font-medium flex items-center gap-2"
                            >
                              <BookOpen className="w-4 h-4" />
                              Study
                            </button>
                            <button
                              onClick={() => {
                                setAskInput(`Tell me about ${readContent.passage}`);
                                setActiveTab("home");
                                setIsReadingPage(false);
                              }}
                              className="px-4 py-2 glass-card border border-[#3B82F6]/30 rounded-xl hover:bg-[#E2E8F0]/30 transition-all text-sm text-[#1E293B] font-medium flex items-center gap-2"
                            >
                              <MessageCircle className="w-4 h-4" />
                              Ask
                            </button>
                          </div>
                          
                          {/* Verse-level text: tap / long-press for highlight menu (persisted when signed in) */}
                          <div
                            ref={bibleReadScrollRef}
                            onScroll={() => {
                              setVerseHighlightMenu(null);
                            }}
                            className="mt-20 min-h-[200px]"
                          >
                            <div
                              ref={homeReadingPassageRef}
                              className="cursor-text select-text"
                            >
                              <ChapterVerseReader
                                text={pageText}
                                highlightsByVerse={chapterVerseHighlights}
                                onPassageMouseUp={handlePassageTextSelect}
                              />
                            </div>
                          </div>
                          
                          {/* Previous / next chapter — bottom of card */}
                          <div className="mt-6 border-t border-[#3B82F6]/25 pt-6">
                            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:gap-4">
                              <button
                                type="button"
                                onClick={handlePrevChapter}
                                disabled={prevDisabled}
                                className="w-full px-3 py-3 glass-card border border-[#3B82F6]/30 rounded-2xl text-sm font-semibold normal-case tracking-normal text-[#1E293B] transition-all hover:bg-[#E2E8F0]/50 disabled:pointer-events-none disabled:opacity-40 sm:flex-1"
                              >
                                {prevNavLabel}
                              </button>
                              <button
                                type="button"
                                onClick={handleNextChapter}
                                disabled={nextDisabled}
                                className="w-full px-3 py-3 glass-card border border-[#3B82F6]/30 rounded-2xl text-sm font-semibold normal-case tracking-normal text-[#1E293B] transition-all hover:bg-[#E2E8F0]/50 disabled:pointer-events-none disabled:opacity-40 sm:flex-1"
                              >
                                {nextNavLabel}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-[#1E293B] mb-4">Read the Bible</h3>

                  {/* Book Browser — Testament + Book dropdowns (same layout as phone on all breakpoints) */}
                  <div className="glass-card rounded-2xl border border-[#3B82F6]/30 p-4 md:p-8">
                    {!selectedBook ? (
                      <div className="space-y-3">
                        <label className="block text-sm font-semibold text-[#475569]">Testament</label>
                        <select
                          value={currentTestament}
                          onChange={(e) => setCurrentTestament(e.target.value)}
                          className="w-full p-3 glass-card border border-[#3B82F6]/30 rounded-xl text-[#1E293B] font-medium focus:outline-none focus:border-[#e2c275]/50"
                        >
                          <option value="OT">Old Testament</option>
                          <option value="NT">New Testament</option>
                        </select>
                        <label className="block text-sm font-semibold text-[#475569]">Book</label>
                        <select
                          value=""
                          onChange={(e) => {
                            const name = e.target.value;
                            if (name) {
                              const book = bibleBooks.find(b => b.name === name);
                              if (book) setSelectedBook(book);
                            }
                          }}
                          className="w-full p-3 glass-card border border-[#3B82F6]/30 rounded-xl text-[#1E293B] font-medium focus:outline-none focus:border-[#e2c275]/50"
                        >
                          <option value="">Choose a book...</option>
                          {bibleBooks.filter(book => book.testament === currentTestament).map((book) => (
                            <option key={book.name} value={book.name}>{book.name}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-6">
                          <h5 className="text-2xl font-bold text-[#1E293B]">{selectedBook.name}</h5>
                          <button
                            onClick={() => {
                              setSelectedBook(null);
                              setSelectedChapter(null);
                            }}
                            className="px-4 py-2 glass-card border border-[#3B82F6]/30 rounded-xl hover:bg-[#E2E8F0]/30 transition-all text-sm text-[#1E293B] font-medium"
                          >
                            ← Back to Books
                          </button>
                        </div>
                        <div className="space-y-3">
                          <label className="block text-sm font-semibold text-[#475569]">Chapter</label>
                          <div
                            className="grid grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3"
                            role="group"
                            aria-label="Choose chapter"
                          >
                            {Array.from({ length: selectedBook.chapters }, (_, i) => i + 1).map((chapterNum) => {
                              const isSelected = selectedChapter === chapterNum;
                              return (
                                <button
                                  key={chapterNum}
                                  type="button"
                                  aria-pressed={isSelected}
                                  onClick={() => {
                                    setSelectedChapter(chapterNum);
                                    loadChapter(selectedBook.name, chapterNum);
                                  }}
                                  className={`p-4 text-base font-semibold rounded-xl transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e2c275]/60 ${
                                    isSelected
                                      ? "bg-[#e2c275] text-black scale-105 shadow-md"
                                      : "glass-card border border-[#3B82F6]/30 hover:bg-[#E2E8F0]/30 hover:border-[#e2c275]/30 hover:shadow-[0_4px_20px_rgba(59,130,246,0.2)] text-[#1E293B]"
                                  }`}
                                >
                                  {chapterNum}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!currentStudy ? (
                <div className="mt-12 md:mt-16 pt-10 md:pt-12 border-t border-[#3B82F6]/25">
                  <div className="mb-8 glass-card rounded-2xl border border-[#3B82F6]/30 p-4 md:p-6 flex flex-col gap-3">
                    <div className="space-y-1.5">
                      <label className="block gold-text text-xs font-bold uppercase tracking-wider">
                        Passage or reference
                      </label>
                      <p className="text-sm text-[#64748B] leading-relaxed">
                        Enter a verse reference or paste the full passage.
                      </p>
                    </div>
                    <textarea
                      value={studyInput}
                      onChange={(e) => setStudyInput(e.target.value)}
                      placeholder="e.g., Romans 8:28, John 3:16-17 — or paste passage text here…"
                      rows={3}
                      className="w-full p-3 text-sm md:text-base glass-card border border-[#3B82F6]/30 rounded-2xl focus:outline-none focus:border-[#e2c275]/50 text-[#1E293B] placeholder:text-[#64748B] bg-[#E2E8F0]/30 resize-y min-h-[72px] max-h-64"
                    />
                  </div>

                  <button
                    onClick={generateStudy}
                    disabled={isStudyLoading}
                    className="w-full py-4 bg-[#e2c275] text-black text-base font-bold rounded-2xl hover:bg-[#e2c275]/90 hover:shadow-[0_4px_20px_rgba(226,194,117,0.4)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase tracking-wider"
                  >
                    {isStudyLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                        Generating Deep Study...
                      </>
                    ) : (
                      <>
                        <BookOpen className="w-5 h-5" />
                        Generate Deep Study
                      </>
                    )}
                  </button>

                  {/* Recent Studies */}
                  {studyHistory.length > 0 && (
                    <div className="mt-8">
                      <h3 className="text-xl font-bold text-[#1E293B] mb-4">
                        Recent Studies
                      </h3>
                      <div className="space-y-3">
                        {studyHistory.slice(0, 5).map((study) => (
                          <button
                            key={study.id}
                            onClick={() => setCurrentStudy(study)}
                            className="w-full p-4 glass-card rounded-2xl border border-[#3B82F6]/30 hover:bg-[#E2E8F0]/30 hover:border-[#e2c275]/30 transition-all text-left"
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-semibold text-[#1E293B]">
                                {study.passage}
                              </span>
                              <span className="text-sm text-[#475569]">
                                {new Date(study.timestamp).toLocaleDateString()}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {/* Study Results */}
                  <div className="mb-6 flex gap-3">
                    <button
                      onClick={() => setCurrentStudy(null)}
                      className="px-6 py-3 glass-card border border-[#3B82F6]/30 rounded-2xl hover:bg-[#E2E8F0]/30 transition-all flex items-center gap-2 text-[#1E293B] text-sm font-semibold uppercase tracking-wider"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      New Study
                    </button>
                    <button
                      onClick={() => saveStudy(currentStudy)}
                      className="px-6 py-3 bg-[#e2c275] text-black rounded-2xl hover:bg-[#e2c275]/90 transition-all flex items-center gap-2 text-sm font-bold uppercase tracking-wider"
                    >
                      <Save className="w-4 h-4" />
                      Save Study
                    </button>
                  </div>

                  <div className="mb-6 p-6 glass-card rounded-2xl border border-[#3B82F6]/30">
                    <h3 className="serif text-2xl font-bold text-[#1E293B] mb-2">
                      {currentStudy.passage}
                    </h3>
                    <p className="text-sm text-[#475569]">
                      {currentStudy.version} •{" "}
                      {new Date(currentStudy.timestamp).toLocaleString()}
                    </p>
                  </div>

                  <div className="prose prose-invert max-w-none mb-8">
                    <div className="whitespace-pre-wrap leading-relaxed text-[#1E293B] text-base">
                      {renderStudyContent(currentStudy.content)}
                    </div>
                  </div>

                  {/* Related Passages */}
                  {currentStudy.relatedPassages &&
                    currentStudy.relatedPassages.length > 0 && (
                      <div className="mb-6 p-6 glass-card rounded-2xl border border-[#3B82F6]/30">
                        <h4 className="text-lg font-bold text-[#1E293B] mb-4 flex items-center gap-2">
                          <LinkIcon className="w-5 h-5 text-[#e2c275]" />
                          Study Related Passages
                        </h4>
                        <div className="flex flex-wrap gap-3">
                          {currentStudy.relatedPassages.map((passage, idx) => (
                            <button
                              key={idx}
                              onClick={() => {
                                setStudyInput(passage);
                                setCurrentStudy(null);
                                setNavigationHistory([
                                  ...navigationHistory,
                                  {
                                    mode: "study",
                                    passage: currentStudy.passage,
                                    timestamp: new Date(),
                                  },
                                ]);
                              }}
                              className="px-4 py-2 glass-card border border-[#e2c275]/30 rounded-xl hover:bg-[#e2c275]/10 hover:border-[#e2c275] transition-all text-sm text-[#1E293B]"
                            >
                              {passage}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Ask Follow-up */}
                  <div className="p-6 glass-card rounded-2xl border border-[#3B82F6]/30">
                    <h4 className="text-lg font-bold text-[#1E293B] mb-3 flex items-center gap-2">
                      <MessageCircle className="w-5 h-5 text-[#e2c275]" />
                      Have follow-up questions?
                    </h4>
                    <p className="text-[#475569] mb-4">
                      Common questions about this passage:
                    </p>
                    <div className="flex flex-wrap gap-3 mb-4">
                      {[
                        "What does this NOT mean?",
                        "How is this relevant today?",
                        "What are different interpretations?",
                      ].map((q, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setAskInput(q + " (" + currentStudy.passage + ")");
                            navigateToAsk({
                              sourceMode: "study",
                              passage: currentStudy.passage,
                              originalQuestion: q,
                            });
                          }}
                          className="px-4 py-2 glass-card border border-[#3B82F6]/30 rounded-xl hover:bg-[#E2E8F0]/30 transition-all text-sm text-[#1E293B]"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() =>
                        navigateToAsk({
                          sourceMode: "study",
                          passage: currentStudy.passage,
                        })
                      }
                      className="w-full py-3 bg-[#e2c275] text-black font-semibold rounded-xl hover:bg-[#e2c275]/90 transition-all uppercase tracking-wider text-sm"
                    >
                      Ask Your Own Question
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* HOME TAB - AI Chatbot Study Session */}
          {activeTab === "home" && (
            <div className="w-full pb-12 pt-5 md:pt-8">
              {/* Header with Stats */}
              <div className="mb-12">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-8 mb-8">
                  <div className="text-center md:text-left">
                    <h2 className="serif text-4xl md:text-5xl font-bold text-[#1E293B] mb-2">
                      Welcome back{userFirstName ? `, ${userFirstName}` : ''}
                    </h2>
                    <p className="text-[#475569] text-base md:text-lg">
                      Your AI-powered Bible study companion
                    </p>
                  </div>
                  
                  {/* Stats: Lucide icons + numbers (Bible version lives on Study / Bible tabs) */}
                  <div className="flex flex-row items-center justify-center md:justify-end gap-6 md:gap-8 shrink-0 mb-6 md:mb-0">
                    <div
                      className="flex items-center gap-2"
                      title="Day streak"
                      aria-label={`Day streak: ${currentStreak} days`}
                    >
                      <Flame
                        className="h-7 w-7 md:h-8 md:w-8 shrink-0 text-[#e2c275]"
                        strokeWidth={2}
                        aria-hidden
                      />
                      <span className="text-2xl md:text-3xl font-bold tabular-nums leading-none text-[#1E293B]">
                        {currentStreak}
                      </span>
                    </div>
                    <div
                      className="flex items-center gap-2"
                      title="Study score"
                      aria-label={`Study score: ${totalStudyDays}`}
                    >
                      <Award
                        className="h-7 w-7 md:h-8 md:w-8 shrink-0 text-[#3B82F6]"
                        strokeWidth={2}
                        aria-hidden
                      />
                      <span className="text-2xl md:text-3xl font-bold tabular-nums leading-none text-[#1E293B]">
                        {totalStudyDays}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chat Interface */}
              <div className="overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 300px)', minHeight: '700px' }}>
                {/* Chat Messages Area */}
                <div
                  ref={homeChatScrollRef}
                  className={`flex-1 p-4 md:p-8 space-y-4 md:space-y-8 scrollbar-hide min-h-0 ${currentAnswer ? 'overflow-y-auto' : 'overflow-y-auto'}`}
                >
                  {!currentAnswer && !pendingQuestion && (
                    <div className="text-center py-6 md:py-16">
                      <div className="inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#e2c275]/10 mb-4 md:mb-8">
                        <MessageCircle className="w-10 h-10 md:w-12 md:h-12 text-[#e2c275]" />
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-[#1E293B] mb-2 md:mb-4">Have a question? </h3>
                      <p
                        className={`text-[#475569] text-base md:text-lg max-w-2xl mx-auto px-2 transition-[margin] duration-300 ${
                          askInput.trim() ? "mb-4 md:mb-6" : "mb-6 md:mb-12"
                        }`}
                      >
                        Ask me anything about the Bible, theology, or Christian living. I'm here to help you study and grow.
                      </p>
                      <div
                        className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-6 max-w-6xl mx-auto px-1 transition-[opacity,max-height] duration-300 ease-out overflow-hidden ${
                          askInput.trim()
                            ? "max-h-0 opacity-0 pointer-events-none select-none"
                            : "max-h-[1200px] opacity-100"
                        }`}
                        aria-hidden={!!askInput.trim()}
                      >
                        {[
                          "What does it mean to have faith?",
                          "How should I handle anxiety?",
                          "What is the Trinity?",
                          "How do I know God's will?",
                        ].map((q, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              setAskInput(q);
                              askQuestion(q);
                            }}
                            className="p-4 md:p-6 glass-card border border-[#3B82F6]/30 rounded-xl md:rounded-2xl hover:bg-[#E2E8F0]/30 hover:border-[#e2c275]/30 transition-all text-left text-sm md:text-base text-[#1E293B]"
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* User Messages - Show immediately when question is asked */}
                  {(currentAnswer || pendingQuestion) && (
                    <>
                      <div className="flex justify-end">
                        <div className="max-w-5xl w-full">
                          <div className="bg-[#e2c275]/20 border border-[#e2c275]/30 rounded-3xl rounded-br-sm p-6">
                            <p className="text-[#1E293B] font-medium text-lg">{currentAnswer?.question || pendingQuestion}</p>
                          </div>
                          <div className="text-xs text-[#64748B] mt-2 text-right">
                            {new Date().toLocaleTimeString()}
                          </div>
                        </div>
                      </div>

                      {/* AI Response - Show typing or full response */}
                      {(isTyping || currentAnswer?.content) && (
                        <div className="flex justify-start">
                          <div className="max-w-5xl w-full">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-8 h-8 rounded-full bg-[#e2c275]/20 flex items-center justify-center">
                                <Sparkles className="w-4 h-4 text-[#e2c275]" />
                              </div>
                              <span className="text-sm font-semibold text-[#1E293B]">Study AI</span>
                            </div>
                            <div className="p-8">
                              <div className="whitespace-pre-wrap leading-relaxed text-[#1E293B] text-lg">
                                {isTyping ? displayedText : (currentAnswer?.content || "")}
                                {isTyping && <span className="animate-pulse">|</span>}
                              </div>
                            </div>
                            <div className="text-xs text-[#64748B] mt-2">
                              {new Date().toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Cited Passages - Only show after typing is complete */}
                      {!isTyping && currentAnswer?.citedPassages && currentAnswer.citedPassages.length > 0 && (
                        <div className="flex justify-start">
                          <div className="max-w-5xl w-full">
                            <div className="p-5">
                              <div className="flex items-center gap-2 mb-3">
                                <BookOpen className="w-4 h-4 text-[#e2c275]" />
                                <span className="text-sm font-semibold text-[#1E293B]">Related Passages</span>
                              </div>
                              <div className="flex flex-wrap gap-3">
                                {currentAnswer.citedPassages.map((passage, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => {
                                      setStudyInput(passage);
                                      setCurrentAnswer(null);
                                      setActiveTab("study");
                                    }}
                                    className="px-4 py-2 glass-card border border-[#e2c275]/30 rounded-xl hover:bg-[#e2c275]/10 hover:border-[#e2c275] transition-all text-sm text-[#e2c275] font-medium"
                                  >
                                    {passage}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {isAskLoading && (
                    <div className="flex justify-start">
                        <div className="max-w-5xl w-full">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-8 h-8 rounded-full bg-[#e2c275]/20 flex items-center justify-center">
                              <Sparkles className="w-4 h-4 text-[#e2c275]" />
                            </div>
                            <span className="text-sm font-semibold text-[#1E293B]">Study AI</span>
                          </div>
                          <div className="p-6">
                            <div className="flex items-center gap-3">
                              <div className="flex gap-1">
                                <div className="w-2 h-2 bg-[#e2c275] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                <div className="w-2 h-2 bg-[#e2c275] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                <div className="w-2 h-2 bg-[#e2c275] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                              </div>
                              <span className="text-[#475569] text-sm">Thinking...</span>
                            </div>
                          </div>
                        </div>
                      </div>
                  )}
                </div>

                {/* Chat Input Area - on mobile: no scroll; shorter placeholder, single-line height */}
                <div className="border-t border-[#3B82F6]/30 p-3 md:p-8">
                  <div className="flex flex-col md:flex-row gap-2 md:gap-6 md:items-end">
                    <div className="flex-1 min-w-0 w-full">
                      <textarea
                        ref={askTextareaRef}
                        value={askInput}
                        onChange={(e) => setAskInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (askInput.trim() && !isAskLoading) {
                              askQuestion();
                            }
                          }
                        }}
                        placeholder="Ask about the Bible, theology, or Christian living..."
                        rows={1}
                        className="w-full py-2.5 px-3 md:py-4 md:px-4 glass-card border border-[#3B82F6]/30 rounded-2xl focus:outline-none focus:border-[#e2c275]/50 text-[#1E293B] placeholder:text-[#64748B] bg-[#E2E8F0]/30 resize-none overflow-x-hidden text-sm md:text-base min-h-[44px] md:min-h-[56px] max-h-[220px] placeholder:text-xs md:placeholder:text-base"
                      />
                    </div>
                    <button
                      onClick={() => askQuestion()}
                      disabled={isAskLoading || !askInput.trim()}
                      className="w-full md:w-auto px-6 py-3 md:px-8 md:py-4 bg-[#e2c275] text-black rounded-2xl hover:bg-[#e2c275]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-semibold uppercase tracking-wider text-xs md:text-sm shrink-0"
                    >
                      {isAskLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <MessageCircle className="w-4 h-4" />
                          Send
                        </>
                      )}
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-1.5 md:mt-3 flex-wrap gap-1">
                    <div className="flex gap-2 md:gap-4 text-[10px] md:text-xs text-[#64748B]">
                      <button
                        onClick={() => {
                          setCurrentAnswer(null);
                          setAskInput("");
                        }}
                        className="hover:text-[#475569] transition-colors bg-transparent border-none outline-none"
                      >
                        New Conversation
                      </button>
                      {currentAnswer && (
                        <button
                          onClick={() => saveAnswer(currentAnswer)}
                          className="hover:text-[#475569] transition-colors flex items-center gap-1"
                        >
                          <Save className="w-3 h-3" />
                          Save
                        </button>
                      )}
                    </div>
                    <div className="text-[10px] md:text-xs text-[#64748B] hidden sm:block">
                      Press Enter to send, Shift+Enter for new line
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* BIBLE TAB */}
          {activeTab === "bible" && (
            <div className="max-w-7xl mx-auto pb-12">
              <div className="mb-8">
                <h2 className="serif text-5xl font-bold text-[#1E293B] mb-4">
                  Bible
                </h2>
                <p className="text-[#475569] text-lg">
                  Read and explore Scripture in your preferred translation
                </p>
              </div>

              {/* Bible Version Selector */}
              <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
                <label className="text-[#475569] text-sm font-semibold uppercase tracking-wider md:shrink-0">
                  Version:
                </label>
                <div className="w-full min-w-0 md:w-auto md:min-w-[280px]">
                  <div className="md:hidden w-full">{renderBibleVersionPickerPhone()}</div>
                  <select
                    value={bibleVersion}
                    onChange={(e) => setBibleVersion(e.target.value)}
                    className="hidden md:block w-full px-4 py-2 glass-card border border-[#3B82F6]/30 rounded-2xl focus:outline-none focus:border-[#e2c275]/50 text-[#1E293B] bg-[#E2E8F0]/30 font-semibold"
                  >
                    {bibleVersions.map((version) => (
                      <option key={version.value} value={version.value} className="bg-[#F0F4F8]">
                        {version.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Passage Lookup */}
              <div className="mb-8">
                <label className="block gold-text text-xs font-bold uppercase tracking-wider mb-3">
                  Enter passage reference
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={readPassage}
                    onChange={(e) => setReadPassage(e.target.value)}
                    placeholder="e.g., John 3:16, Psalm 23, Romans 8:1-17"
                    className="flex-1 p-4 glass-card border border-[#3B82F6]/30 rounded-2xl focus:outline-none focus:border-[#e2c275]/50 text-[#1E293B] placeholder:text-[#64748B] bg-[#E2E8F0]/30"
                    onKeyPress={(e) => e.key === "Enter" && lookupPassage()}
                  />
                  <button
                    onClick={lookupPassage}
                    className="px-8 py-4 bg-[#e2c275] text-black font-semibold rounded-2xl hover:bg-[#e2c275]/90 transition-all flex items-center gap-2 uppercase tracking-wider text-sm"
                  >
                    <Search className="w-5 h-5" />
                    Lookup
                  </button>
                </div>
              </div>

              {/* Popular Passages */}
              {!readContent && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-[#1E293B] mb-4">Popular Passages</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      { ref: "John 3:16", title: "God's Love" },
                      { ref: "Psalm 23", title: "The Lord is My Shepherd" },
                      { ref: "Romans 8:28", title: "All Things Work Together" },
                      { ref: "Philippians 4:13", title: "I Can Do All Things" },
                      { ref: "Jeremiah 29:11", title: "Plans to Prosper" },
                      { ref: "1 Corinthians 13", title: "The Love Chapter" },
                    ].map((passage, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setReadPassage(passage.ref);
                          lookupPassage();
                        }}
                        className="p-4 glass-card border border-[#3B82F6]/30 rounded-2xl hover:bg-[#E2E8F0]/30 hover:border-[#e2c275]/30 transition-all text-left gsap-card"
                      >
                        <div className="font-bold text-[#1E293B] mb-1">{passage.ref}</div>
                        <div className="text-sm text-[#475569]">{passage.title}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Passage Display */}
              {readContent && (
                <div>
                  <div className="mb-6 p-6 glass-card rounded-3xl border border-[#3B82F6]/30">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="serif text-2xl font-bold text-[#1E293B] mb-2">
                          {readContent.passage}
                        </h3>
                        <p className="text-sm text-[#475569]">
                          {readContent.version}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={studyFromRead}
                          className="px-4 py-2 glass-card border border-[#3B82F6]/30 rounded-xl hover:bg-[#E2E8F0]/30 transition-all text-[#1E293B] text-sm font-medium"
                        >
                          <BookOpen className="w-4 h-4 inline mr-2" />
                          Study
                        </button>
                        <button
                          onClick={() => {
                            setAskInput(`Tell me about ${readContent.passage}`);
                            setActiveTab("home");
                          }}
                          className="px-4 py-2 glass-card border border-[#3B82F6]/30 rounded-xl hover:bg-[#E2E8F0]/30 transition-all text-[#1E293B] text-sm font-medium"
                        >
                          <MessageCircle className="w-4 h-4 inline mr-2" />
                          Ask
                        </button>
                      </div>
                    </div>
                  </div>

                  <div
                    onMouseUp={handlePassageTextSelect}
                    onTouchEnd={handlePassageTextSelect}
                    className="mb-6 cursor-text select-text rounded-3xl border border-[#3B82F6]/30 bg-[#F8FAFC]/40 p-8 glass-card"
                  >
                    <p
                      ref={bibleTabPassageRef}
                      className="serif text-xl leading-relaxed whitespace-pre-wrap text-[#1E293B]"
                    >
                      {renderPassageWithHighlights(readContent.text, passageHighlights)}
                    </p>
                  </div>

                  {/* Navigation */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setReadPassage("")}
                      className="px-6 py-3 glass-card border border-[#3B82F6]/30 rounded-2xl hover:bg-[#E2E8F0]/30 transition-all text-[#1E293B] text-sm font-semibold uppercase tracking-wider"
                    >
                      New Search
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* LIBRARY TAB */}
          {activeTab === "library" && (
            <div className="max-w-7xl mx-auto pb-12">
              {/* Header */}
              <div className="mb-12">
                <h2 className="serif text-5xl font-bold text-[#1E293B] mb-4">
                  My Library
                </h2>
                <p className="text-[#475569] text-lg">
                  Your saved studies, notes, and study history
                </p>
              </div>

              {/* Tab Navigation */}
              <div className="flex gap-3 mb-8 pb-4 border-b border-[#3B82F6]/30 overflow-x-auto scrollbar-hide">
                <button 
                  className={`px-6 py-3 rounded-2xl font-semibold uppercase tracking-wider text-sm whitespace-nowrap transition-all flex items-center gap-2 ${
                    libraryView === "studies" ? 'bg-[#e2c275] text-black' : 'glass-card border border-[#3B82F6]/30 text-[#475569] hover:bg-[#E2E8F0]/30'
                  }`}
                  onClick={() => {
                    setLibraryView("studies");
                    setShowNoteEditor(false);
                  }}
                >
                  <BookOpen className="w-4 h-4" />
                  Saved Studies
                  {savedStudies.length > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs ${libraryView === "studies" ? 'bg-[#B8860B]/20' : 'bg-[#E2E8F0]/40'}`}>
                      {savedStudies.length}
                    </span>
                  )}
                </button>
                <button 
                  className={`px-6 py-3 rounded-2xl font-semibold uppercase tracking-wider text-sm whitespace-nowrap transition-all flex items-center gap-2 ${
                    libraryView === "answers" ? 'bg-[#e2c275] text-black' : 'glass-card border border-[#3B82F6]/30 text-[#475569] hover:bg-[#E2E8F0]/30'
                  }`}
                  onClick={() => {
                    setLibraryView("answers");
                    setShowNoteEditor(false);
                  }}
                >
                  <MessageCircle className="w-4 h-4" />
                  Saved Answers
                  {savedAnswers.length > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs ${libraryView === "answers" ? 'bg-[#B8860B]/20' : 'bg-[#E2E8F0]/40'}`}>
                      {savedAnswers.length}
                    </span>
                  )}
                </button>
                <button 
                  className={`px-6 py-3 rounded-2xl font-semibold uppercase tracking-wider text-sm whitespace-nowrap transition-all flex items-center gap-2 ${
                    libraryView === "notes" ? 'bg-[#e2c275] text-black' : 'glass-card border border-[#3B82F6]/30 text-[#475569] hover:bg-[#E2E8F0]/30'
                  }`}
                  onClick={() => {
                    setLibraryView("notes");
                    setShowNoteEditor(true);
                  }}
                >
                  <FileText className="w-4 h-4" />
                  Notes
                  {notes.length > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs ${libraryView === "notes" ? 'bg-[#B8860B]/20' : 'bg-[#E2E8F0]/40'}`}>
                      {notes.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Saved Studies Section */}
              {libraryView === "studies" && !showNoteEditor && (
                <div>
                  {/* Search Bar */}
                  <div className="mb-6">
                    <div className="relative max-w-md">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                      <input
                        type="text"
                        placeholder="Search saved studies..."
                        className="w-full pl-12 pr-4 py-3 glass-card border border-[#3B82F6]/30 rounded-2xl focus:outline-none focus:border-[#e2c275]/50 text-[#1E293B] placeholder:text-[#64748B] bg-[#E2E8F0]/30"
                      />
                    </div>
                  </div>

                  {/* Saved Studies Grid */}
                  {savedStudies.length === 0 ? (
                    <div className="text-center py-20 glass-card rounded-3xl border border-[#3B82F6]/30">
                      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#E2E8F0]/30 mb-6">
                        <Save className="w-10 h-10 text-[#64748B]" />
                      </div>
                      <h3 className="text-xl font-semibold text-[#1E293B] mb-2">No saved studies yet</h3>
                      <p className="text-[#475569] mb-6 max-w-md mx-auto">
                        Save your favorite studies to access them anytime. Click "Save Study" when viewing a study.
                      </p>
                      <button
                        onClick={() => setActiveTab("study")}
                        className="px-6 py-3 bg-[#e2c275] text-black rounded-2xl hover:bg-[#e2c275]/90 transition-all font-semibold uppercase tracking-wider text-sm inline-flex items-center gap-2"
                      >
                        <BookOpen className="w-4 h-4" />
                        Start a Study
                      </button>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {savedStudies.map((study) => (
                        <div
                          key={study.id}
                          className="group p-6 glass-card rounded-3xl border border-[#3B82F6]/30 hover:bg-[#E2E8F0]/30 hover:border-[#e2c275]/30 transition-all cursor-pointer gsap-card"
                          onClick={() => {
                            setCurrentStudy(study);
                            setActiveTab("study");
                          }}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="text-lg font-bold text-[#1E293B] mb-2 group-hover:text-[#e2c275] transition-colors">
                                {study.passage}
                              </h3>
                              <div className="flex items-center gap-2 text-xs text-[#1E293B]/50">
                                <BookOpen className="w-3 h-3" />
                                <span>{study.version}</span>
                              </div>
                            </div>
                            <div className="p-2 rounded-xl bg-[#E2E8F0]/30 group-hover:bg-[#e2c275]/10 transition-colors">
                              <ArrowRight className="w-4 h-4 text-[#475569] group-hover:text-[#e2c275] transition-colors" />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[#64748B]">
                            <Clock className="w-3 h-3" />
                            <span>{new Date(study.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Saved Answers Section */}
              {libraryView === "answers" && !showNoteEditor && (
                <div>
                  {/* Search Bar */}
                  <div className="mb-6">
                    <div className="relative max-w-md">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                      <input
                        type="text"
                        placeholder="Search saved answers..."
                        className="w-full pl-12 pr-4 py-3 glass-card border border-[#3B82F6]/30 rounded-2xl focus:outline-none focus:border-[#e2c275]/50 text-[#1E293B] placeholder:text-[#64748B] bg-[#E2E8F0]/30"
                      />
                    </div>
                  </div>

                  {/* Saved Answers Grid */}
                  {savedAnswers.length === 0 ? (
                    <div className="text-center py-20 glass-card rounded-3xl border border-[#3B82F6]/30">
                      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#E2E8F0]/30 mb-6">
                        <MessageCircle className="w-10 h-10 text-[#64748B]" />
                      </div>
                      <h3 className="text-xl font-semibold text-[#1E293B] mb-2">No saved answers yet</h3>
                      <p className="text-[#475569] mb-6 max-w-md mx-auto">
                        Save your favorite AI chat answers to access them anytime. Click "Save" when viewing an answer.
                      </p>
                      <button
                        onClick={() => setActiveTab("home")}
                        className="px-6 py-3 bg-[#e2c275] text-black rounded-2xl hover:bg-[#e2c275]/90 transition-all font-semibold uppercase tracking-wider text-sm inline-flex items-center gap-2"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Start a Conversation
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {savedAnswers.map((answer) => (
                        <div
                          key={answer.id}
                          className="p-6 glass-card rounded-3xl border border-[#3B82F6]/30 hover:bg-[#E2E8F0]/30 hover:border-[#e2c275]/30 transition-all"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-8 rounded-full bg-[#e2c275]/20 flex items-center justify-center">
                                  <MessageCircle className="w-4 h-4 text-[#e2c275]" />
                                </div>
                                <h3 className="text-lg font-bold text-[#1E293B]">Q: {answer.question}</h3>
                              </div>
                              <div className="text-sm text-[#475569] mb-4 line-clamp-3">
                                {answer.content}
                              </div>
                              {answer.citedPassages && answer.citedPassages.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-4">
                                  {answer.citedPassages.map((passage, idx) => (
                                    <span
                                      key={idx}
                                      className="px-3 py-1 glass-card border border-[#e2c275]/30 rounded-xl text-sm text-[#e2c275] font-medium"
                                    >
                                      {passage}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between pt-4 border-t border-[#3B82F6]/30">
                            <div className="flex items-center gap-2 text-xs text-[#64748B]">
                              <Clock className="w-3 h-3" />
                              <span>{new Date(answer.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                            </div>
                            <button
                              onClick={() => {
                                setCurrentAnswer(answer);
                                setActiveTab("home");
                              }}
                              className="px-4 py-2 glass-card border border-[#3B82F6]/30 rounded-xl hover:bg-[#e2c275]/10 hover:border-[#e2c275] transition-all text-sm text-[#1E293B] font-medium flex items-center gap-2"
                            >
                              View Full Answer
                              <ArrowRight className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Notes Section */}
              {libraryView === "notes" && showNoteEditor && (
                <div>
                  {/* Notes Header with Search */}
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                    <div className="relative flex-1 max-w-md">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                      <input
                        type="text"
                        value={noteSearch}
                        onChange={(e) => setNoteSearch(e.target.value)}
                        placeholder="Search notes..."
                        className="w-full pl-12 pr-4 py-3 glass-card border border-[#3B82F6]/30 rounded-2xl focus:outline-none focus:border-[#e2c275]/50 text-[#1E293B] placeholder:text-[#64748B] bg-[#E2E8F0]/30"
                      />
                    </div>
                    <button
                      onClick={() => {
                        setShowNoteEditor(true);
                        setCurrentNote(null);
                        setNoteTitle("");
                        setNoteContent("");
                        setNoteTags("");
                      }}
                      className="px-6 py-3 bg-[#e2c275] text-black rounded-2xl hover:bg-[#e2c275]/90 transition-all flex items-center gap-2 font-semibold uppercase tracking-wider text-sm whitespace-nowrap"
                    >
                      <Plus className="w-4 h-4" />
                      New Note
                    </button>
                  </div>

                  {/* Note Editor */}
                  {showNoteEditor && (currentNote || (!currentNote && (noteTitle || noteContent || noteTags))) && (
                    <div className="mb-8 p-8 glass-card rounded-3xl border border-[#3B82F6]/30">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 rounded-xl bg-[#e2c275]/10">
                          <PenTool className="w-5 h-5 text-[#e2c275]" />
                        </div>
                        <h4 className="text-xl font-bold text-[#1E293B]">
                          {currentNote ? "Edit Note" : "New Note"}
                        </h4>
                      </div>

                      <input
                        type="text"
                        value={noteTitle}
                        onChange={(e) => setNoteTitle(e.target.value)}
                        placeholder="Note title..."
                        className="w-full p-4 mb-4 glass-card border border-[#3B82F6]/30 rounded-2xl focus:outline-none focus:border-[#e2c275]/50 text-[#1E293B] placeholder:text-[#64748B] bg-[#E2E8F0]/30"
                      />

                      <textarea
                        value={noteContent}
                        onChange={(e) => setNoteContent(e.target.value)}
                        placeholder="Write your study notes, reflections, or insights..."
                        rows="8"
                        className="w-full p-4 mb-4 glass-card border border-[#3B82F6]/30 rounded-2xl focus:outline-none focus:border-[#e2c275]/50 text-[#1E293B] placeholder:text-[#64748B] bg-[#E2E8F0]/30 resize-none"
                      />

                      <input
                        type="text"
                        value={noteTags}
                        onChange={(e) => setNoteTags(e.target.value)}
                        placeholder="Tags (comma-separated, e.g., prayer, faith, hope)"
                        className="w-full p-4 mb-6 glass-card border border-[#3B82F6]/30 rounded-2xl focus:outline-none focus:border-[#e2c275]/50 text-[#1E293B] placeholder:text-[#64748B] bg-[#E2E8F0]/30"
                      />

                      <div className="flex gap-3">
                        <button
                          onClick={currentNote ? updateNote : createNote}
                          className="px-6 py-3 bg-[#e2c275] text-black rounded-2xl hover:bg-[#e2c275]/90 transition-all font-semibold uppercase tracking-wider text-sm flex items-center gap-2"
                        >
                          <Save className="w-4 h-4" />
                          {currentNote ? "Update" : "Save"} Note
                        </button>
                        <button
                          onClick={resetNoteEditor}
                          className="px-6 py-3 glass-card border border-[#3B82F6]/30 text-[#1E293B] rounded-2xl hover:bg-[#E2E8F0]/30 transition-all font-semibold uppercase tracking-wider text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Notes Grid */}
                  {filteredNotes.length === 0 ? (
                    <div className="text-center py-20 glass-card rounded-3xl border border-[#3B82F6]/30">
                      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#E2E8F0]/30 mb-6">
                        <FileText className="w-10 h-10 text-[#64748B]" />
                      </div>
                      <h3 className="text-xl font-semibold text-[#1E293B] mb-2">
                        {notes.length === 0 ? "No notes yet" : "No notes match your search"}
                      </h3>
                      <p className="text-[#475569] mb-6 max-w-md mx-auto">
                        {notes.length === 0 
                          ? "Start capturing your thoughts and insights. Create your first note to begin building your personal study journal."
                          : "Try adjusting your search terms or create a new note."}
                      </p>
                      {notes.length === 0 && (
                        <button
                          onClick={() => {
                            setShowNoteEditor(true);
                            setCurrentNote(null);
                            setNoteTitle("");
                            setNoteContent("");
                            setNoteTags("");
                          }}
                          className="px-6 py-3 bg-[#e2c275] text-black rounded-2xl hover:bg-[#e2c275]/90 transition-all font-semibold uppercase tracking-wider text-sm inline-flex items-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Create First Note
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredNotes.map((note) => (
                        <div
                          key={note.id}
                          className="group p-6 glass-card rounded-3xl border border-[#3B82F6]/30 hover:bg-[#E2E8F0]/30 hover:border-[#e2c275]/30 transition-all gsap-card"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h4 className="text-lg font-bold text-[#1E293B] mb-2 group-hover:text-[#e2c275] transition-colors">
                                {note.title}
                              </h4>
                              <div className="flex items-center gap-2 text-xs text-[#64748B] mb-3">
                                <Clock className="w-3 h-3" />
                                <span>{new Date(note.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => editNote(note)}
                                className="p-2 text-[#e2c275] hover:bg-[#e2c275]/10 rounded-xl transition-all"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteNote(note.id)}
                                className="p-2 text-red-400 hover:bg-red-400/10 rounded-xl transition-all"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <p className="text-[#475569] mb-4 line-clamp-3 leading-relaxed">
                            {note.content}
                          </p>
                          {note.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {note.tags.map((tag, idx) => (
                                <span
                                  key={idx}
                                  className="px-3 py-1 glass-card border border-[#e2c275]/30 text-[#e2c275] text-xs rounded-xl font-medium"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* GUIDED STUDY TAB */}
          {activeTab === "guided" && (
            <div className="max-w-7xl mx-auto pb-12">
              {guidedView === "in_plan" && activePlanProgress ? (
                <>
                  <div className="mb-6">
                    <button
                      onClick={() => {
                        if (guidedDaySession) {
                          setGuidedDaySession(null);
                          setGuidedSessionVerseText(null);
                          setGuidedSessionSectionResponse("");
                        } else {
                          setGuidedView("plans");
                        }
                      }}
                      className="px-4 py-2 glass-card border border-[#3B82F6]/30 rounded-xl hover:bg-[#E2E8F0]/30 transition-all text-sm text-[#1E293B] font-medium flex items-center gap-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      {guidedDaySession ? "Back to day" : "Study Plans"}
                    </button>
                  </div>

                  {guidedDaySession ? (() => {
                    const { planId, day } = guidedDaySession;
                    const planTitle = activePlanProgress?.planTitle || "Guided Study";
                    const totalDays = activePlanProgress?.totalDays || 1;
                    const daysConfig = planDaysConfig[planId] || [];
                    const d = daysConfig.find((x) => x.day === day) || { day, title: `Day ${day}`, scriptureRef: "", quote: "", sections: ["Scripture", "Reflect", "Personal Activation"] };
                    const sections = d.sections || ["Scripture", "Reflect", "Personal Activation"];
                    const sectionIndex = Math.min(guidedDaySession.sectionIndex, sections.length - 1);
                    const sectionName = sections[sectionIndex];
                    const scriptureRefs = (d.scriptureRef || "").split(",").map((s) => s.trim()).filter((s) => s && s !== "—");
                    const isScriptureSection = sectionName === "Scripture" || sectionName.toLowerCase().includes("scripture");
                    const currentStep = sectionIndex + 1;
                    const totalSteps = sections.length;
                    const primaryRef = scriptureRefs[0] || d.scriptureRef || "";

                    const goNextSection = () => {
                      // If leaving Personal Activation with a response, save to Library → Notes
                      if (sectionName === "Personal Activation") {
                        if (guidedSessionSectionResponse && guidedSessionSectionResponse.trim()) {
                          const note = {
                            id: Date.now(),
                            title: `${planTitle} · Day ${day} – Personal Activation`,
                            content: guidedSessionSectionResponse.trim(),
                            tags: ["guided study", "personal activation"],
                            timestamp: new Date().toISOString(),
                            lastModified: new Date().toISOString(),
                            linkedPassage: primaryRef || null,
                          };
                          setNotes((prev) => [note, ...prev]);
                        }
                        setGuidedSessionSectionResponse("");
                      }
                      if (sectionIndex + 1 >= sections.length) {
                        setActivePlanProgress((prev) => prev ? { ...prev, completedDays: [...(prev.completedDays || []), day], currentDay: Math.min(prev.currentDay + 1, prev.totalDays) } : null);
                        setGuidedDaySession(null);
                        setGuidedSessionVerseText(null);
                        setGuidedSessionSectionResponse("");
                      } else {
                        setGuidedDaySession((prev) => prev ? { ...prev, sectionIndex: prev.sectionIndex + 1, scriptureIndex: 0 } : null);
                        setGuidedSessionVerseText(null);
                      }
                    };

                    const nextScripture = () => {
                      if (guidedDaySession.scriptureIndex + 1 < scriptureRefs.length) {
                        setGuidedDaySession((prev) => prev ? { ...prev, scriptureIndex: prev.scriptureIndex + 1 } : null);
                        setGuidedSessionVerseText(null);
                      } else {
                        setGuidedDaySession((prev) => prev ? { ...prev, sectionIndex: prev.sectionIndex + 1, scriptureIndex: 0 } : null);
                        setGuidedSessionVerseText(null);
                      }
                    };

                    const goToSection = (idx) => {
                      setGuidedDaySession((prev) => prev ? { ...prev, sectionIndex: idx, scriptureIndex: 0 } : null);
                      setGuidedSessionVerseText(null);
                    };

                    return (
                      <div className="max-w-3xl mx-auto space-y-6">
                        {/* Top header: plan · day | circular progress (back is in main nav) */}
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1" />
                          <p className="text-sm md:text-base font-semibold uppercase tracking-wider text-[#64748B] flex-1 text-center truncate">
                            {planTitle.replace(/:.*/, "")} · Day {day}
                          </p>
                          <div className="relative w-14 h-14 shrink-0 flex items-center justify-center flex-1">
                            <svg className="w-14 h-14 -rotate-90" viewBox="0 0 36 36">
                              <path fill="none" stroke="#cbd5e1" strokeWidth="2.5" d="M18 2.5 a 15.5 15.5 0 0 1 0 31 a 15.5 15.5 0 0 1 0 -31" />
                              <path fill="none" stroke="#e2c275" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="97, 100" d="M18 2.5 a 15.5 15.5 0 0 1 0 31 a 15.5 15.5 0 0 1 0 -31" style={{ strokeDasharray: `${(currentStep / totalSteps) * 97}, 100` }} />
                            </svg>
                            <span className="absolute text-base font-bold text-[#1E293B]">{currentStep}/{totalSteps}</span>
                          </div>
                        </div>

                        {/* Day info card: Day X of Y, title, ref, description, section dots (display only) */}
                        <div className="rounded-2xl border-2 border-[#e2c275]/50 bg-[#e2c275]/15 shadow-lg p-6 md:p-8">
                          <p className="text-sm font-semibold uppercase tracking-wider text-[#64748B] mb-1">Day {day} of {totalDays}</p>
                          <h2 className="serif text-2xl md:text-4xl font-bold text-[#1E293B] mb-2">Day {day}: {d.title}</h2>
                          <p className={`text-base text-[#64748B] ${d.quote ? "mb-3" : "mb-6"}`}>{primaryRef ? `${primaryRef} · ${bibleVersion}` : ""}</p>
                          {d.quote ? <p className="text-[#475569] text-base md:text-lg italic border-l-2 border-[#e2c275]/40 pl-4 mb-6 serif-scripture">"{d.quote}"</p> : null}
                          {/* Section tabs: Scripture · Guided Reflection · Personal Activation (clickable) */}
                          <div className="flex flex-col w-full pt-4">
                            <div className="relative flex justify-between items-stretch w-full" style={{ height: "12px" }}>
                              <div
                                className="absolute left-0 right-0 h-0.5 bg-[#E2E8F0]/80"
                                style={{ top: "5px" }}
                              />
                              {sections.map((sec, i) => (
                                <button
                                  key={sec}
                                  type="button"
                                  onClick={() => goToSection(i)}
                                  className="relative z-10 flex justify-center flex-1 min-w-0 items-center cursor-pointer touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e2c275]/60 focus-visible:ring-offset-2 rounded"
                                >
                                  <span className={`block w-3 h-3 rounded-full shrink-0 leading-none ${i === sectionIndex ? "bg-[#e2c275] shadow-[0_0_8px_rgba(226,194,117,0.5)]" : "bg-[#E2E8F0]"}`} />
                                </button>
                              ))}
                            </div>
                            <div className="flex justify-between w-full mt-2 gap-1">
                              {sections.map((sec, i) => (
                                <button
                                  key={sec}
                                  type="button"
                                  onClick={() => goToSection(i)}
                                  className={`flex-1 min-w-0 text-xs sm:text-sm font-medium text-center px-2 py-1.5 rounded-lg transition-colors cursor-pointer touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e2c275]/60 focus-visible:ring-offset-2 ${i === sectionIndex ? "text-[#1E293B] bg-[#e2c275]/20 border border-transparent" : "text-[#94A3B8] hover:text-[#64748B] hover:bg-[#E2E8F0]/50 border border-[#cbd5e1]/70"}`}
                                >
                                  {sec}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Section header: checkmark, SECTION N, section name (Next is on verse/content area) */}
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500/90 flex items-center justify-center shrink-0">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-[#64748B]">Section {sectionIndex + 1}</p>
                            <p className="serif text-lg md:text-xl font-bold text-emerald-700">{sectionName}</p>
                          </div>
                        </div>

                        {isScriptureSection ? (
                          <div className="rounded-2xl border-2 border-[#3B82F6]/20 bg-[#E2E8F0]/20 p-8 md:p-10">
                            {scriptureRefs.length === 0 ? (
                              <div className="flex flex-col gap-5">
                                <p className="text-[#64748B] text-base">No scripture for this section.</p>
                                <button onClick={goNextSection} className="self-start px-6 py-3 bg-[#e2c275] text-black rounded-xl font-bold uppercase tracking-wider text-sm hover:bg-[#e2c275]/90">Next section</button>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                                  <p className="gold-text text-base md:text-lg font-bold uppercase tracking-wider min-w-0">
                                    {scriptureRefs[guidedDaySession.scriptureIndex]} ({bibleVersion})
                                  </p>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <button
                                      onClick={() => {
                                        const ref = scriptureRefs[guidedDaySession.scriptureIndex];
                                        if (ref) {
                                          setStudyInput(ref);
                                          setActiveTab("study");
                                        }
                                      }}
                                      className="px-4 py-2.5 bg-white border border-[#3B82F6]/30 rounded-xl hover:bg-[#E2E8F0]/50 transition-all text-sm text-[#1E293B] font-medium flex items-center gap-2"
                                    >
                                      <BookOpen className="w-4 h-4" />
                                      Study
                                    </button>
                                    <button
                                      onClick={() => {
                                        const ref = scriptureRefs[guidedDaySession.scriptureIndex];
                                        if (ref) {
                                          setAskInput(`Tell me about ${ref}`);
                                          setActiveTab("home");
                                        }
                                      }}
                                      className="px-4 py-2.5 bg-white border border-[#3B82F6]/30 rounded-xl hover:bg-[#E2E8F0]/50 transition-all text-sm text-[#1E293B] font-medium flex items-center gap-2"
                                    >
                                      <MessageCircle className="w-4 h-4" />
                                      Ask
                                    </button>
                                    <button onClick={nextScripture} className="px-5 py-2.5 bg-[#1E293B] text-white rounded-lg font-semibold text-sm hover:bg-[#334155] transition-all">
                                      Next
                                    </button>
                                  </div>
                                </div>
                                {guidedSessionVerseLoading ? (
                                  <p className="text-[#64748B] text-lg md:text-xl py-8">Loading…</p>
                                ) : guidedSessionVerseText ? (
                                  <div className="serif-scripture text-lg md:text-xl text-[#1E293B] font-medium leading-relaxed whitespace-pre-wrap">{guidedSessionVerseText}</div>
                                ) : null}
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="rounded-2xl border-2 border-[#3B82F6]/20 bg-[#E2E8F0]/30 p-6 md:p-8 space-y-5">
                            {(sectionName === "Reflect" || sectionName === "Guided Reflection") ? (
                              <>
                                <div className="serif-scripture text-base md:text-lg text-[#1E293B] leading-relaxed whitespace-pre-wrap">
                                  {d.guidedReflection ? (
                                    (() => {
                                      const firstNewline = d.guidedReflection.indexOf("\n");
                                      const firstLine = firstNewline > 0 ? d.guidedReflection.slice(0, firstNewline) : d.guidedReflection;
                                      const rest = firstNewline > 0 ? d.guidedReflection.slice(firstNewline) : "";
                                      return (
                                        <>
                                          <span className="font-bold">{firstLine}</span>
                                          {rest}
                                        </>
                                      );
                                    })()
                                  ) : (
                                    <>
                                      <span className="font-bold">What is worship?</span>
                                      {"\n\nPause. Don't rush this.\n\nDon't give the church answer.\nGive the honest one.\n\nThere is no point in knowing what worship is if we have not made it the priority of our lives.\n\nThis is not a surface conversation.\nThis is not about music preference.\nThis is not about Sunday attendance.\n\nThis is about hierarchy.\n\nWorship reveals what sits highest in your heart.\n\nIf worship is occasional, then God is occasional.\nIf worship is emotional, then God is seasonal.\nIf worship is convenient, then God is secondary."}
                                    </>
                                  )}
                                </div>
                                <div className="flex justify-center">
                                  <button onClick={goNextSection} className="min-w-[220px] px-12 py-4 bg-[#e2c275] text-black rounded-xl font-bold uppercase tracking-wider text-base hover:bg-[#e2c275]/90 transition-all">
                                    {sectionIndex >= sections.length - 1 ? "Finish day" : "Continue"}
                                  </button>
                                </div>
                              </>
                            ) : (
                              <>
                                {sectionName === "Prayer" ? (
                                  <div className="space-y-4">
                                    <p className="serif-scripture text-base md:text-lg text-[#1E293B] leading-relaxed whitespace-pre-line italic">
                                      "Lord, teach us to number our days.{"\n"}
                                      Reorder our priorities.{"\n"}
                                      Give us courage to say no where necessary.{"\n"}
                                      Restore reverence in our hearts.{"\n"}
                                      And help us worship You not occasionally — but positionally."
                                    </p>
                                    <p className="serif text-lg md:text-xl font-semibold text-[#1E293B]">
                                      Journal your personal prayer as you close this study
                                    </p>
                                  </div>
                                ) : (
                                  <p className={`serif text-lg md:text-xl font-semibold text-[#1E293B] ${sectionName === "Personal Activation" && d.personalActivationPrompt ? "whitespace-pre-line" : ""}`}>
                                    {sectionName === "Personal Activation" ? (d.personalActivationPrompt || "Write how you will put this into practice.") : sectionName}
                                  </p>
                                )}
                                {!(sectionName === "Personal Activation" && day === 5) && (
                                  <textarea
                                    value={guidedSessionSectionResponse}
                                    onChange={(e) => setGuidedSessionSectionResponse(e.target.value)}
                                    placeholder="Type your response here…"
                                    className="w-full min-h-[160px] p-5 rounded-xl border-2 border-[#3B82F6]/25 bg-white text-[#1E293B] placeholder:text-[#94A3B8] resize-y text-base md:text-lg serif-scripture leading-relaxed"
                                    rows={5}
                                  />
                                )}
                                <div className="flex justify-center">
                                  <button onClick={goNextSection} className="min-w-[220px] px-12 py-4 bg-[#e2c275] text-black rounded-xl font-bold uppercase tracking-wider text-base hover:bg-[#e2c275]/90 transition-all">
                                    {sectionIndex >= sections.length - 1 ? "Finish day" : "Continue"}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })() : (() => {
                    const { planId, planTitle, totalDays, currentDay, completedDays } = activePlanProgress;
                    const completedCount = completedDays.length;
                    const daysConfig = planDaysConfig[planId] || Array.from({ length: totalDays }, (_, i) => ({ day: i + 1, title: `Day ${i + 1}`, scriptureRef: "", quote: "" }));
                    const weekTotal = Math.ceil(totalDays / 3) || 1;
                    const currentWeek = Math.min(Math.ceil(currentDay / 7) || 1, weekTotal);
                    const progressLabels = ["Prepare", "Encounter", "Transform"];
                    const getCurrentSegmentIndex = () => {
                      const end1 = Math.ceil(totalDays / 3);
                      const end2 = Math.ceil((2 * totalDays) / 3);
                      if (currentDay <= end1) return 0;
                      if (currentDay <= end2) return 1;
                      return 2;
                    };
                    const currentSegmentIndex = getCurrentSegmentIndex();

                    return (
                      <div className="max-w-4xl mx-auto space-y-6 px-2">
                        <h2 className="serif text-2xl md:text-4xl font-bold text-[#1E293B]">{planTitle}</h2>

                        <section>
                          <p className="gold-text text-xs font-bold uppercase tracking-wider mb-2">Your progress</p>
                          <div className="flex flex-wrap items-baseline gap-2 mb-3">
                            <span className="text-[#1E293B] font-bold text-lg">Week {currentWeek} of {weekTotal}</span>
                            <span className="text-[#64748B] text-sm">
                              <span className="text-[#e2c275] font-semibold">{completedCount}</span>/{totalDays} days complete
                            </span>
                          </div>
                          <div className="relative h-4 rounded-full overflow-hidden">
                            <div className="absolute inset-0 flex">
                              {progressLabels.map((label) => (
                                <div key={label} className="flex-1 h-full bg-[#cbd5e1] border-r border-[#1E293B]/10 last:border-r-0 first:rounded-l-full last:rounded-r-full" />
                              ))}
                            </div>
                            <div
                              className="absolute inset-y-0 left-0 rounded-l-full bg-[#e2c275] transition-all duration-300"
                              style={{ width: `${(completedCount / totalDays) * 100}%` }}
                            />
                          </div>
                          <div className="flex mt-2">
                            {progressLabels.map((label) => (
                              <div key={label} className="flex-1 text-center">
                                <span className="text-xs font-semibold uppercase tracking-wider text-[#475569]">{label}</span>
                              </div>
                            ))}
                          </div>
                        </section>

                        <div className="space-y-4">
                          {daysConfig.map((d) => {
                            const isCompleted = completedDays.includes(d.day);
                            const isCurrent = currentDay === d.day;
                            const openDay = () => {
                              setGuidedDaySession({ planId, day: d.day, sectionIndex: 0, scriptureIndex: 0 });
                              setGuidedSessionVerseText(null);
                              setGuidedSessionSectionResponse("");
                            };
                            return (
                              <div
                                key={d.day}
                                role={isCompleted ? "button" : undefined}
                                tabIndex={isCompleted ? 0 : undefined}
                                onClick={isCompleted ? openDay : undefined}
                                onKeyDown={isCompleted ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDay(); } } : undefined}
                                className={`rounded-2xl border p-5 md:p-6 transition-all ${
                                  isCurrent ? "border-[#e2c275]/50 bg-[#e2c275]/10" : "border-[#3B82F6]/25 bg-[#E2E8F0]/50"
                                } ${isCompleted ? "cursor-pointer hover:bg-[#E2E8F0]/70 hover:border-[#3B82F6]/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e2c275]/60 focus-visible:ring-offset-2" : ""}`}
                              >
                                <div className="flex items-center gap-5">
                                  <div className={`shrink-0 w-14 h-14 rounded-full flex items-center justify-center text-lg ${
                                    isCompleted ? "bg-emerald-500/90 text-white" : isCurrent ? "bg-[#e2c275] text-black font-bold" : "bg-[#E2E8F0]/60 text-[#64748B] font-semibold"
                                  }`}>
                                    {isCompleted ? <Check className="w-6 h-6" /> : d.day}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-[#1E293B] text-base md:text-lg">Day {d.day}: {d.title}</p>
                                    <p className="text-sm text-[#64748B] mt-0.5">{d.scriptureRef}</p>
                                  </div>
                                </div>

                                {isCurrent && (
                                  <>
                                    <p className="mt-5 text-base text-[#475569] italic border-l-2 border-[#e2c275]/40 pl-5">"{d.quote}"</p>
                                    <div className="mt-5 flex flex-col w-full">
                                      <div className="relative flex justify-between items-stretch w-full" style={{ height: "10px" }}>
                                        <div className="absolute left-0 right-0 h-0.5 bg-[#E2E8F0]/80" style={{ top: "4px" }} />
                                        {(d.sections || ["Read", "Reflect", "Pray", "Journal"]).map((action, i) => (
                                          <div key={`${d.day}-${action}-${i}`} className="relative z-10 flex justify-center flex-1 min-w-0 items-center">
                                            <span className={`block w-2.5 h-2.5 rounded-full shrink-0 leading-none ${i === 0 ? "bg-[#e2c275] shadow-[0_0_8px_rgba(226,194,117,0.5)]" : "bg-[#E2E8F0]"}`} />
                                          </div>
                                        ))}
                                      </div>
                                      <div className="flex justify-between w-full mt-2">
                                        {(d.sections || ["Read", "Reflect", "Pray", "Journal"]).map((action, i) => (
                                          <span key={`${d.day}-${action}-${i}`} className={`flex-1 min-w-0 text-xs sm:text-sm font-medium text-center px-0.5 ${i === 0 ? "text-[#1E293B]" : "text-[#64748B]"}`}>{action}</span>
                                        ))}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => {
                                        setGuidedDaySession({ planId, day: d.day, sectionIndex: 0, scriptureIndex: 0 });
                                        setGuidedSessionVerseText(null);
                                        setGuidedSessionSectionResponse("");
                                      }}
                                      className="mt-4 w-full py-3 bg-[#e2c275] text-black rounded-xl font-semibold uppercase tracking-wider text-sm hover:bg-[#e2c275]/90 transition-all"
                                    >
                                      {d.day === 1 ? "Start Study" : "Continue Study"}
                                    </button>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <>
                  {/* Guided Study Plans: header + featured + categories + grid */}
                  <div className="mb-12">
                    <h2 className="serif text-5xl font-bold text-[#1E293B] mb-4">
                      Guided Study Plans
                    </h2>
                    <p className="text-[#475569] text-lg">
                      Structured study plans to deepen your understanding of Scripture
                    </p>
                  </div>

                  {/* Featured Plan */}
                  <div className="mb-12">
                    <div className="glass-card rounded-3xl border border-[#e2c275]/30 p-8 md:p-12 overflow-hidden relative">
                      <div className="absolute top-0 right-0 w-64 h-64 bg-[#e2c275]/5 rounded-full blur-3xl -mr-32 -mt-32"></div>
                      <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="px-4 py-2 bg-[#e2c275]/20 border border-[#e2c275]/30 rounded-full">
                            <span className="gold-text text-xs font-bold uppercase tracking-wider">Featured</span>
                          </div>
                          <span className="text-[#475569] text-sm">5 Days</span>
                        </div>
                        <h3 className="serif text-3xl md:text-4xl font-bold text-[#1E293B] mb-3">
                          Worship: The Ultimate Priority
                        </h3>
                        <p className="text-[#475569] text-lg mb-6 max-w-2xl">
                          A 5-day guided journey that moves worship from an occasional event to the
                          ultimate priority—through reverence, time, boundaries, and daily surrender.
                        </p>
                        <div className="flex flex-wrap items-center gap-6 mb-6">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-[#e2c275]" />
                            <span className="text-[#475569] text-sm">5 days</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-[#e2c275]" />
                            <span className="text-[#475569] text-sm">Mark 12:30 + key passages</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="w-5 h-5 text-[#e2c275]" />
                            <span className="text-[#475569] text-sm">Barry A. Williams Jr. </span>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (activePlanProgress?.planId === "worship_priority") {
                              setGuidedView("in_plan");
                            } else {
                              setActiveGuidedPlan({
                                id: "worship_priority",
                                title: "Worship: The Ultimate Priority",
                                days: 5,
                              });
                            }
                          }}
                          className="px-8 py-4 bg-[#e2c275] text-black rounded-2xl hover:bg-[#e2c275]/90 transition-all font-semibold uppercase tracking-wider text-sm inline-flex items-center gap-2"
                        >
                          {activePlanProgress?.planId === "worship_priority" ? "Continue" : "View Overview"}
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Plan Categories */}
                  <div className="mb-8">
                    <div className="flex gap-3 mb-6 overflow-x-auto scrollbar-hide pb-2">
                      <button
                        onClick={() => setGuidedCategory("all")}
                        className={`px-6 py-3 rounded-2xl font-semibold uppercase tracking-wider text-sm whitespace-nowrap transition-all ${
                          guidedCategory === "all"
                            ? "bg-[#e2c275] text-black"
                            : "glass-card border border-[#3B82F6]/30 text-[#475569] hover:bg-[#E2E8F0]/30"
                        }`}
                      >
                        All Plans
                      </button>
                      <button
                        onClick={() => setGuidedCategory("new_testament")}
                        className={`px-6 py-3 rounded-2xl font-semibold uppercase tracking-wider text-sm whitespace-nowrap transition-all ${
                          guidedCategory === "new_testament"
                            ? "bg-[#e2c275] text-black"
                            : "glass-card border border-[#3B82F6]/30 text-[#475569] hover:bg-[#E2E8F0]/30"
                        }`}
                      >
                        New Testament
                      </button>
                      <button
                        onClick={() => setGuidedCategory("old_testament")}
                        className={`px-6 py-3 rounded-2xl font-semibold uppercase tracking-wider text-sm whitespace-nowrap transition-all ${
                          guidedCategory === "old_testament"
                            ? "bg-[#e2c275] text-black"
                            : "glass-card border border-[#3B82F6]/30 text-[#475569] hover:bg-[#E2E8F0]/30"
                        }`}
                      >
                        Old Testament
                      </button>
                      <button
                        onClick={() => setGuidedCategory("topical")}
                        className={`px-6 py-3 rounded-2xl font-semibold uppercase tracking-wider text-sm whitespace-nowrap transition-all ${
                          guidedCategory === "topical"
                            ? "bg-[#e2c275] text-black"
                            : "glass-card border border-[#3B82F6]/30 text-[#475569] hover:bg-[#E2E8F0]/30"
                        }`}
                      >
                        Topical
                      </button>
                      <button
                        onClick={() => setGuidedCategory("community")}
                        className={`px-6 py-3 rounded-2xl font-semibold uppercase tracking-wider text-sm whitespace-nowrap transition-all ${
                          guidedCategory === "community"
                            ? "bg-[#e2c275] text-black"
                            : "glass-card border border-[#3B82F6]/30 text-[#475569] hover:bg-[#E2E8F0]/30"
                        }`}
                      >
                        Community
                      </button>
                    </div>
                  </div>

                  {/* Study Plans Grid */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {(() => {
                      const filtered =
                        guidedCategory === "all"
                          ? guidedStudyPlans
                          : guidedStudyPlans.filter(
                              (plan) => plan.category === guidedCategory
                            );

                      if (guidedCategory === "community") {
                        return (
                          <div className="col-span-full text-center py-16 glass-card rounded-3xl border border-[#3B82F6]/30">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#E2E8F0]/40 mb-4">
                              <Users className="w-8 h-8 text-[#e2c275]" />
                            </div>
                            <h3 className="serif text-2xl font-bold text-[#1E293B] mb-2">
                              Community plans are coming soon
                            </h3>
                            <p className="text-[#475569] max-w-md mx-auto text-sm">
                              Join guided studies created by the Light community. You’ll soon be
                              able to browse and contribute community-led journeys here.
                            </p>
                          </div>
                        );
                      }

                      if (filtered.length === 0) {
                        return (
                          <div className="col-span-full text-center py-16 glass-card rounded-3xl border border-[#3B82F6]/30">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#E2E8F0]/40 mb-4">
                              <BookOpen className="w-8 h-8 text-[#e2c275]" />
                            </div>
                            <h3 className="serif text-2xl font-bold text-[#1E293B] mb-2">
                              More plans coming soon
                            </h3>
                            <p className="text-[#475569] max-w-md mx-auto text-sm">
                              We're adding more guided study plans. Start with the featured plan above.
                            </p>
                          </div>
                        );
                      }

                      return filtered.map((plan) => (
                        <div
                          key={plan.id}
                          className="group glass-card rounded-3xl border border-[#3B82F6]/30 hover:bg-[#E2E8F0]/30 hover:border-[#e2c275]/30 transition-all cursor-pointer gsap-card overflow-hidden"
                        >
                          <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                              <div className="px-3 py-1 bg-[#E2E8F0]/30 rounded-full">
                                <span className="text-[#e2c275] text-xs font-semibold uppercase">
                                  {plan.days} Days
                                </span>
                              </div>
                              <BookOpen className="w-5 h-5 text-[#64748B]" />
                            </div>
                            <h4 className="text-xl font-bold text-[#1E293B] mb-2 group-hover:text-[#e2c275] transition-colors">
                              {plan.title}
                            </h4>
                            <p className="text-[#475569] text-sm mb-4 line-clamp-2">
                              {plan.blurb}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-[#64748B] mb-4">
                              <Calendar className="w-3 h-3" />
                              <span>{plan.reference}</span>
                            </div>
                            <button
                              onClick={() => setActiveGuidedPlan(plan)}
                              className="w-full py-2.5 glass-card border border-[#3B82F6]/30 rounded-xl hover:bg-[#E2E8F0]/30 transition-all text-[#1E293B] text-sm font-medium"
                            >
                              View Plan
                            </button>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>

                  {/* My Active Plans */}
                  <div className="mt-16">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="serif text-3xl font-bold text-[#1E293B]">My Active Plans</h3>
                    </div>
                    {activePlanProgress ? (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setGuidedView("in_plan")}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setGuidedView("in_plan"); } }}
                        className="group glass-card rounded-3xl border border-[#3B82F6]/30 hover:bg-[#E2E8F0]/30 hover:border-[#e2c275]/30 transition-all cursor-pointer p-6 flex items-center gap-5"
                      >
                        <div className="w-14 h-14 rounded-xl bg-[#e2c275]/20 border border-[#e2c275]/40 flex items-center justify-center shrink-0">
                          <BookOpen className="w-7 h-7 text-[#e2c275]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-xl font-bold text-[#1E293B] mb-0.5 group-hover:text-[#e2c275] transition-colors">
                            {activePlanProgress.planTitle}
                          </h4>
                          <p className="text-[#64748B] text-sm">
                            Day {activePlanProgress.currentDay} of {activePlanProgress.totalDays} · {(activePlanProgress.completedDays || []).length} completed
                          </p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-[#64748B] shrink-0 group-hover:text-[#e2c275] transition-colors" />
                      </div>
                    ) : (
                      <div className="text-center py-12 glass-card rounded-3xl border border-[#3B82F6]/30">
                        <BookOpen className="w-16 h-16 mx-auto mb-4 text-[#1E293B]/20" />
                        <p className="text-[#475569] mb-2">No active plans</p>
                        <p className="text-[#64748B] text-sm">Start a plan above to add it here</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* PROFILE TAB */}
          {activeTab === "profile" && (
            <div className="max-w-7xl mx-auto pb-12">
              {/* Profile Section */}
              <div className="mb-12">
                <div className="glass-card rounded-3xl border border-[#3B82F6]/30 p-6 md:p-8">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                    <div className="flex flex-col items-center sm:items-start gap-2 shrink-0">
                      <div className="w-20 h-20 rounded-full overflow-hidden bg-[#e2c275]/20 flex items-center justify-center ring-2 ring-[#3B82F6]/20">
                        {profileAvatarUrl ? (
                          <img src={profileAvatarUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-10 h-10 text-[#e2c275]" />
                        )}
                      </div>
                      {user && (
                        <>
                          <input
                            ref={profileAvatarInputRef}
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            aria-label="Upload profile photo"
                            onChange={handleProfileAvatarChange}
                          />
                          <button
                            type="button"
                            onClick={() => profileAvatarInputRef.current?.click()}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3B82F6] hover:text-[#2563EB] transition-colors"
                          >
                            <Camera className="w-3.5 h-3.5" />
                            Change photo
                          </button>
                        </>
                      )}
                    </div>
                    <div className="min-w-0">
                      <h2 className="serif text-2xl md:text-3xl font-bold text-[#1E293B] mb-1">
                        {user ? (userFirstName || user.email?.split('@')[0] || 'My Profile') : 'My Profile'}
                      </h2>
                      {user?.email && (
                        <p className="text-[#475569] text-sm mb-2">{user.email}</p>
                      )}
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className="text-[#1E293B] font-semibold">{currentStreak} day streak</span>
                        <span className="text-[#64748B]">•</span>
                        <span className="text-[#1E293B] font-semibold">{totalStudyDays} study days</span>
                        {isPremium && (
                          <>
                            <span className="text-[#64748B]">•</span>
                            <span className="text-[#e2c275] font-semibold">Premium</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Badges */}
                <div className="mt-8">
                  <h3 className="text-xl font-bold text-[#1E293B] mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-[#e2c275]" />
                    Badges
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {badgeDefinitions.map((badge) => {
                      const earned = badges.includes(badge.id);
                      return (
                        <div
                          key={badge.id}
                          className={`glass-card rounded-2xl border p-4 text-center transition-all ${
                            earned
                              ? 'border-[#e2c275]/50 bg-[#e2c275]/5'
                              : 'border-[#3B82F6]/20 opacity-60'
                          }`}
                        >
                          <div className="text-2xl mb-2">{badge.icon}</div>
                          <div className="font-semibold text-[#1E293B] text-sm">{badge.name}</div>
                          <div className="text-[10px] text-[#475569] mt-0.5">{badge.description}</div>
                          {!earned && (
                            <div className="text-[10px] text-[#64748B] mt-1">Locked</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Mobile Navigation */}
      <div
        className="md:hidden fixed left-7 right-7 z-50 rounded-full border border-[#3B82F6]/22 bg-[rgba(15,23,42,0.22)] shadow-[0_10px_36px_rgba(15,23,42,0.28)] ring-1 ring-inset ring-white/[0.08]"
        style={{
          WebkitBackdropFilter: "blur(22px) saturate(1.35)",
          backdropFilter: "blur(22px) saturate(1.35)",
          transform: bottomTabsCompact ? "scale(0.88)" : "scale(1)",
          transformOrigin: "center bottom",
          transition: "transform 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
          pointerEvents: "auto",
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)",
        }}
      >
        <div
          className={`flex items-center justify-between gap-0.5 transition-[padding] duration-200 ${
            bottomTabsCompact ? "px-2 py-0.5" : "px-2.5 py-1"
          }`}
        >
          <button
            onClick={() => setActiveTab("home")}
            className={`h-9 w-9 rounded-full flex items-center justify-center transition-all ${
              activeTab === "home" ? "bg-[#64748B]/40" : "hover:bg-white/[0.06]"
            }`}
          >
            <Home className={`w-[18px] h-[18px] ${activeTab === "home" ? "text-[#e2c275]" : "text-[#E2E8F0]/90"}`} />
          </button>
          <button
            onClick={() => setActiveTab("study")}
            className={`h-9 w-9 rounded-full flex items-center justify-center transition-all ${
              activeTab === "study" ? "bg-[#64748B]/40" : "hover:bg-white/[0.06]"
            }`}
          >
            <PenTool className={`w-[18px] h-[18px] ${activeTab === "study" ? "text-[#e2c275]" : "text-[#E2E8F0]/90"}`} />
          </button>
          <button
            onClick={() => setActiveTab("library")}
            className={`h-9 w-9 rounded-full flex items-center justify-center transition-all ${
              activeTab === "library" ? "bg-[#64748B]/40" : "hover:bg-white/[0.06]"
            }`}
          >
            <BookMarked className={`w-[18px] h-[18px] ${activeTab === "library" ? "text-[#e2c275]" : "text-[#E2E8F0]/90"}`} />
          </button>
          <button
            onClick={() => { setActiveTab("guided"); setGuidedView("plans"); }}
            className={`h-9 w-9 rounded-full flex items-center justify-center transition-all ${
              activeTab === "guided" ? "bg-[#64748B]/40" : "hover:bg-white/[0.06]"
            }`}
          >
            <ListChecks className={`w-[18px] h-[18px] ${activeTab === "guided" ? "text-[#e2c275]" : "text-[#E2E8F0]/90"}`} />
          </button>
          <button
            onClick={() => setActiveTab("profile")}
            className={`h-9 w-9 rounded-full flex items-center justify-center transition-all ${
              activeTab === "profile" ? "bg-[#64748B]/40" : "hover:bg-white/[0.06]"
            }`}
          >
            <User className={`w-[18px] h-[18px] ${activeTab === "profile" ? "text-[#e2c275]" : "text-[#E2E8F0]/90"}`} />
          </button>
        </div>
      </div>

      {/* Footer — desktop/tablet only; hidden on phone for a more native-app shell */}
      <footer className="hidden md:block relative z-10 py-10 md:py-20 border-t border-[#3B82F6]/25 mt-0">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 mb-8 md:mb-20">
            <div className="col-span-2 md:col-span-2">
              <div className="flex items-center gap-2 mb-3 md:mb-6">
                <h1 className="text-xl md:text-2xl font-light tracking-widest uppercase text-[#1E293B]">Ligh<span className="text-[#e2c275] text-2xl md:text-3xl">✝</span></h1>
              </div>
              <p className="text-[#64748B] text-sm md:text-base max-w-sm leading-snug md:leading-loose">
                Providing AI-powered tools that transform Bible reading into profound study since 2026. Designed for the followers and seekers of God's Word to mature in their Christian journey.
              </p>
            </div>

            <div>
              <h5 className="gold-text text-xs uppercase font-bold mb-3 md:mb-6">Resources</h5>
              <ul className="space-y-2 md:space-y-4 text-sm md:text-base text-[#475569]">
                <li><a href="#" className="hover:text-[#e2c275] transition-colors">Library</a></li>
                <li><a href="#" className="hover:text-[#e2c275] transition-colors">Podcasts</a></li>
                <li><a href="#" className="hover:text-[#e2c275] transition-colors">Study Tools</a></li>
              </ul>
            </div>

            <div>
              <h5 className="gold-text text-xs uppercase font-bold mb-3 md:mb-6">Connect</h5>
              <ul className="space-y-2 md:space-y-4 text-sm md:text-base text-[#475569]">
                <li><a href="#" className="hover:text-[#e2c275] transition-colors">Community</a></li>
                <li><a href="#" className="hover:text-[#e2c275] transition-colors">Events</a></li>
                <li><a href="#" className="hover:text-[#e2c275] transition-colors">Support</a></li>
              </ul>
            </div>
          </div>

          <div className="flex flex-row justify-between items-center gap-2 md:gap-6 pt-6 md:pt-12 mt-6 md:mt-8 border-t border-[#3B82F6]/25 text-[10px] md:text-xs uppercase tracking-widest text-[#1E293B]/30">
            <p className="shrink min-w-0">© 2026 Ligh✝<span className="hidden sm:inline">. All Rights Reserved.</span></p>
            <div className="flex gap-3 md:gap-8 shrink-0">
              <a href="#" className="hover:text-[#1E293B]/50 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-[#1E293B]/50 transition-colors">Terms of Sanctuary</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LightBibleStudy;
